class Engine {
    constructor(inits, user, pkg, config, game, refCanvas, react) {
        this.inits = inits;
        this.user = user;
        this.pkg = pkg;
        this.config = config;
        this.game = game;
        this.react = react;

        this.engineBaseSpeed = 0.016;

        this.delta = 0;

        this.personajes = {};

        this.lastDelta = 0;
        this.timerTicksPerFrame = 0;
        this.lFrameTimer = 0;
        this.framesPerSecCounter = 0;
        this.FPS = 0;
        this.ping = 0;

        this.offsetCounterX = 0;
        this.offsetCounterY = 0;

        // Ticket #238: bumped 9 → 10 para acelerar el smooth scroll
        // (~200ms por tile en vez de ~222ms). Va con timeWalkMS=200 en
        // config.js — los 3 valores (este + timeWalkMS + threshold del
        // anti-cheat server) se mueven juntos.
        this.scrollPixelsPerFrameX = 10;
        this.scrollPixelsPerFrameY = 10;

        // Ticket #225: cache de posiciones de items por mapa para evitar
        // que updateItems itere 33×33 = 1089 tiles cada frame durante
        // movimiento. Estructura: { [mapNumber]: [{ y, x }, ...] }.
        // Populado lazy en el primer call de updateItems para un mapa
        // (one-time scan de los 10000 tiles), mantenido al día por
        // _addItemPosition / _removeItemPosition cuando llegan packets
        // renderItem / deleteItem.
        this._itemPositionsByMap = {};

        // Cache de sprites tintados (off-screen canvases). Cuando un sprite
        // tiene tint (ej. portales Efímero/Sueño/Sangre), aplicar ctx.filter
        // cada frame cuesta ~0.5-1ms por sprite porque corre un shader pass.
        // Cacheando el sprite ya tintado en un canvas off-screen evitamos el
        // filter pass — solo blit directo desde el cache. ~10× más barato.
        //
        // Key format: "numFile:sX:sY:width:height:tint" — incluye la región
        // del tilesheet original + el tipo de tint. Los frames distintos del
        // mismo animado tienen keys distintos (sX/sY varían) y se cachean
        // por separado.
        //
        // Memoria: ~700 bytes por sprite tintado (43×64×4 RGBA). Para los 8
        // frames × 3 colores (azul/verde/rojo) ≈ 17KB total. Trivial.
        // Build lazy: la primera vez que se ve un portal tintado, build de
        // ~1ms; después gratis.
        this._tintedSpriteCache = {};

        // ──────────────────────────────────────────────────────────────
        // Cache de text labels off-screen (perf crítico).
        //
        // Bug reportado por zArko (2026-06-19): "carga demasiadas imágenes
        // por seg cuando aparecen NPCs, baja FPS". DevTools mostraba ~180k
        // drawcalls/seg en zonas pobladas.
        //
        // Root cause: renderText2 (sobre .foreground) hacía ctx.fillText +
        // ctx.strokeText por cada nombre + clan de cada char/NPC en
        // viewport, cada frame. En zona con 30 NPCs = 60 calls × 60fps =
        // 3,600 fillText/seg. fillText cuesta 0.2-1ms = 720-3600ms/seg
        // SOLO en text.
        //
        // Fix: pre-renderear cada label único (text+color+font) a un canvas
        // off-screen, blittear con drawImage en cada frame. drawImage es
        // 10-50× más barato que fillText. Cache LRU cap 200 entries para
        // no leakear (nombres únicos cambian cuando entran NPCs nuevos al
        // viewport).
        //
        // Hit rate esperado: ~100% para nombres de NPCs estáticos. ~85% para
        // nombres de chars (cambia por color cuando GM toggle, criminal,
        // etc — cada combo es entry separado).
        //
        // Memoria: ~200 entries × ~200 bytes promedio = ~40KB. Trivial.
        this._textCanvasCache = new Map();
        this._textCacheCap = 200;

        // Offscreen canvas reusable para tintar FX (drawFx con fx.tint:'red').
        // Una sola instancia por engine — se reusa cambiando width/height por
        // call. Evita allocs per-frame en spells tinteadas. Cap: 256x256 max.
        this._tintOffscreen = null;

        this.posYDescClient = 0;
        this.sumPosY = 0;
        this.lifeDescClient = 0;
        this.timeMoveDescClient = 0;

        this.timeWalk = 0;

        this.canvas = {};
        this.refCanvas = refCanvas;

        // Estado de teclas movement+usar. `keydown` dict por keyCode →
        // bool (held). `keyOrder` es el STACK ordenado por press time —
        // la tecla más reciente queda al final. Cuando se evalúa el move
        // en el game loop, walk de end → start y la primer tecla de
        // dirección encontrada gana.
        //
        // Ticket #210: antes el game loop usaba `if/else if` con orden
        // hardcoded (left → right → up → down), lo que pisaba dirección
        // a dirección. Holdear A y tapar W resultaba en A ganando.
        // El stack hace que "la última tecla apretada manda" (input model
        // estándar de action RPGs, equivalente al AO clásico).
        this.keydown = {};
        this.keyOrder = [];

        // Si el foco está en un input/textarea que NO sea el chat del
        // juego (data-game-key="true"), skipear los handlers de movement.
        // Sino, tipear WASD en cualquier modal (party invite, drop item,
        // macro config, etc) hace caminar al char. El chat del juego se
        // exime con data-game-key="true" porque tiene su propia lógica.
        const isInExternalInput = () => {
            const ae = document.activeElement;
            if (
                !ae ||
                (ae.tagName !== "INPUT" &&
                    ae.tagName !== "TEXTAREA" &&
                    !ae.isContentEditable)
            ) {
                return false;
            }
            return !(ae.dataset && ae.dataset.gameKey === "true");
        };

        // HIGH-22 fix: handlers guardados como `this._foo` para poder
        // hacer remove en `destroy()`. Antes eran anónimos y cada engine
        // instance creada por logout/reconnect dejaba sus listeners stuck
        // en `document` indefinidamente.
        this._handlerKeydown = e => {
            if (isInExternalInput()) return;
            const KC = this.react.state.keyCodeDefault;
            // Solo movement + usar (índices 0..4) entran al tracker.
            if (
                e.keyCode == KC[0] ||
                e.keyCode == KC[1] ||
                e.keyCode == KC[2] ||
                e.keyCode == KC[3] ||
                e.keyCode == KC[4]
            ) {
                // Detectar PRIMER press (no los auto-repeat del browser).
                // Sin esta guard, cada tick de auto-repeat pushea la misma
                // tecla al stack y se hace un mess. Idempotente: si ya
                // estaba held, no hacer nada.
                if (!this.keydown[e.keyCode]) {
                    this.keydown[e.keyCode] = true;
                    this.keyOrder.push(e.keyCode);
                }
            }
        };
        document.addEventListener("keydown", this._handlerKeydown);

        this._handlerKeyup = e => {
            // KeyUp NO skipea por input focus — si la tecla está stuck
            // en this.keydown (ej. user soltó W con el modal abierto),
            // debemos liberarla sí o sí. Sino el char camina infinito
            // hasta cerrar el modal y volver a apretar+soltar la tecla.
            const KC = this.react.state.keyCodeDefault;
            if (
                e.keyCode == KC[0] ||
                e.keyCode == KC[1] ||
                e.keyCode == KC[2] ||
                e.keyCode == KC[3] ||
                e.keyCode == KC[4]
            ) {
                if (this.keydown[e.keyCode]) {
                    this.keydown[e.keyCode] = false;
                    const idx = this.keyOrder.indexOf(e.keyCode);
                    if (idx !== -1) this.keyOrder.splice(idx, 1);
                }
            }
        };
        document.addEventListener("keyup", this._handlerKeyup);

        // Cuando el tab vuelve a foreground, reset el reloj de delta así
        // el primer frame de vuelta animal por ~16ms (un frame normal) en
        // vez de N segundos que estuvo hidden. Sin esto, aún con el cap
        // de 50ms en el loop hay un mini-salto visible al volver del
        // bg-tab porque el delta acumulado iría al máximo permitido.
        this._handlerVisibilityChange = () => {
            if (!document.hidden) {
                this.lastDelta = this.timestamp();
            } else {
                // Tab-away: liberar TODAS las teclas. Sin esto, si el user
                // tabea al medio de un W, el keyup nunca dispara (browser
                // suspende eventos del tab background) y al volver el char
                // camina solo eternamente hasta que toque+suelte la tecla.
                // Ticket #210.
                this.keydown = {};
                this.keyOrder = [];
            }
        };
        document.addEventListener(
            "visibilitychange",
            this._handlerVisibilityChange
        );
    }

    // Llamado desde play/index.js → componentWillUnmount para limpiar
    // listeners globales. Sin esto, una engine vieja sigue respondiendo a
    // todos los keystrokes después de un logout — multiplica el CPU per
    // keypress proporcional a cuántos logouts hubo. Idempotente.
    // v2026-06-26: subida NATURAL del skill Magia al castear un hechizo.
    // Implementación fiel a la lógica de Tierras del Sur / AO Libre
    // (Modulo_UsUaRiOs.bas:SubirSkill). Probabilística + cap por nivel.
    //
    // Cap por nivel:
    //   Par:    floor(lvl/2) × 5
    //   Impar:  floor(lvl/2) × 5 + 3 − (((floor((lvl-1)/2) × 5) Mod 10) / 5)
    //   Lvl 1→3, 2→5, 3→7, 4→10, 5→13, ..., 40→100
    //
    // Probabilidad por cast:
    //   range = (Int(0.1 × lvl² + 15)) × 2  ← DificultadSubirSkill = 2
    //   roll  = 1..range
    //   cutoff = 10 (no-experto)  → success si roll < cutoff
    //   P(rise) = 9 / range
    //   Lvl 1: 30%, Lvl 20: 8.2%, Lvl 40: 2.6%
    _tryRiseMagia = () => {
        try {
            if (!this.react || !this.react.state) return;
            const savedSkills = this.react.state.savedSkills || {};
            const curMagia = savedSkills.magia || 0;
            if (curMagia >= 100) return; // cap absoluto, premium 101 solo via Morgolock
            const lvl = (this.user && this.user.level) || 1;

            // Cap por nivel (formula VB6)
            let maxPermitido;
            if (lvl % 2 === 0) {
                maxPermitido = Math.floor(lvl / 2) * 5;
            } else {
                const aux = Math.floor((lvl - 1) / 2) * 5;
                maxPermitido = Math.floor(lvl / 2) * 5 + 3 - Math.floor((aux % 10) / 5);
            }
            if (curMagia >= maxPermitido) return;

            // Roll probabilístico
            const prob = Math.floor(0.1 * lvl * lvl + 15);
            const range = prob * 2; // DificultadSubirSkill = 2
            const aumenta = Math.floor(Math.random() * range) + 1;
            const cutoff = 10; // NONEXPERT_SKILL_CUTOFF
            if (aumenta >= cutoff) return;

            // ¡Subió!
            const newMagia = curMagia + 1;
            const newSaved = { ...savedSkills, magia: newMagia };
            const curSkills = this.react.state.skills || {};
            // skills (pending) también sube — si tenía pending > saved en magia,
            // se preserva el delta. Aquí simplemente seteamos a max(skills, newMagia).
            const newSkillsVal = Math.max((curSkills.magia || 0), newMagia);
            const newSkills = { ...curSkills, magia: newSkillsVal };
            this.react.setState({ savedSkills: newSaved, skills: newSkills });
            try {
                localStorage.setItem("savedSkills", JSON.stringify(newSaved));
                localStorage.setItem("skills", JSON.stringify(newSkills));
            } catch (e) {}

            if (this.game && this.game.writeConsole) {
                this.game.writeConsole(
                    "¡Has mejorado tu skill Magia en un punto!. Ahora tienes " + newMagia + " pts.",
                    "#00ced1"   // turquesa azul (estilo AO classic / TDS Legacy)
                );
            }
        } catch (err) {
            dumpError(err);
        }
    };

    destroy = () => {
        // Perf 2026-07-07: frenar el RAF loop — ver guard en loop().
        this._destroyed = true;
        if (this._rafId) {
            cancelAnimationFrame(this._rafId);
            this._rafId = null;
        }
        if (this._handlerKeydown) {
            document.removeEventListener("keydown", this._handlerKeydown);
            this._handlerKeydown = null;
        }
        if (this._handlerKeyup) {
            document.removeEventListener("keyup", this._handlerKeyup);
            this._handlerKeyup = null;
        }
        if (this._handlerVisibilityChange) {
            document.removeEventListener(
                "visibilitychange",
                this._handlerVisibilityChange
            );
            this._handlerVisibilityChange = null;
        }
    };

    initCanvas = async () => {
        this.canvas.background = {};
        this.canvas.background.ctx = this.refCanvas.background.getContext(
            "2d",
            {
                alpha: false
            }
        );

        this.canvas.techos = {};
        this.canvas.techos.ctx = this.refCanvas.techos.getContext("2d");

        this.canvas.foreground = {};
        this.canvas.foreground.ctx = this.refCanvas.foreground.getContext("2d");

        this.canvas.items = {};
        this.canvas.items.ctx = this.refCanvas.items.getContext("2d");

        this.canvas.textos = {};
        this.canvas.textos.ctx = this.refCanvas.textos.getContext("2d");

        // Ticket #296: desactivar smoothing en todos los contexts cuando
        // el flag pixelPerfect está ON (default). El juego es pixel art
        // 32×32; con smoothing=true (default del browser) los bordes del
        // sprite se interpolan al renderear en coords sub-pixel
        // (offsetCounterX float). Combina con el floor de drawGrhCapa
        // para eliminar las "líneas negras entre tiles al caminar".
        //
        // Si el user apaga el toggle en Settings, dejamos imageSmoothing
        // en su default (true) — render más "smooth" pero con halo/gaps
        // visibles durante el movimiento. El toggle se propaga vía
        // react.togglePixelPerfect que también re-aplica esto live.
        const pp = this.pixelPerfect !== false;
        const ctxs = [
            this.canvas.background.ctx,
            this.canvas.techos.ctx,
            this.canvas.foreground.ctx,
            this.canvas.items.ctx,
            this.canvas.textos.ctx
        ];
        for (const ctx of ctxs) {
            if (ctx) {
                ctx.imageSmoothingEnabled = !pp;
                // Prefixes para browsers más viejos, no rompe si no existen.
                ctx.mozImageSmoothingEnabled = !pp;
                ctx.webkitImageSmoothingEnabled = !pp;
                ctx.msImageSmoothingEnabled = !pp;
            }
        }

        const loadMaps = this.inits.loadMaps();
        const loadObjs = this.inits.loadObjs();
        const loadHeads = this.inits.loadHeads();
        const loadArmas = this.inits.loadArmas();
        const loadEscudos = this.inits.loadEscudos();
        const loadCascos = this.inits.loadCascos();
        const loadBodies = this.inits.loadBodies();
        const loadFxs = this.inits.loadFxs();
        const loadGraphics = this.inits.loadGraphics();

        await Promise.all([
            loadMaps,
            loadObjs,
            loadHeads,
            loadArmas,
            loadEscudos,
            loadCascos,
            loadBodies,
            loadFxs,
            loadGraphics
        ]);
    };

    timestamp = () => {
        return window.performance && window.performance.now
            ? window.performance.now()
            : new Date().getTime();
    };

    cleanMapData = idMap => {
        if (this.inits.mapData[idMap]) {
            for (let y = 1; y <= 100; y++) {
                for (let x = 1; x <= 100; x++) {
                    this.inits.mapData[idMap][y][x].id = 0;
                    delete this.inits.mapa[idMap][y][x].o;
                }
            }
        }
    };

    setMapData = (idMap, pos, data) => {
        try {
            const map = this.inits.mapData[idMap];
            if (!map || !map[pos.y] || !map[pos.y][pos.x]) return;
            map[pos.y][pos.x].id = data;
        } catch (err) {
            dumpError(err);
        }
    };

    clickCanvas = pos => {
        try {
            // Mapa puede no estar cargado, click puede caer fuera del grid
            // 1..100 si user.pos no está seteado o se clickeó en una esquina.
            const map = this.inits.mapData[this.config.mapNumber];
            if (!map) return;

            const inBounds = (x, y) =>
                y >= this.config.YMinMapSize &&
                y <= this.config.YMaxMapSize &&
                x >= this.config.XMinMapSize &&
                x <= this.config.XMaxMapSize &&
                map[y] &&
                map[y][x];

            if (!inBounds(pos.x, pos.y)) return;

            let tileSelected = map[pos.y][pos.x];

            if (!tileSelected.id && inBounds(pos.x, pos.y + 1)) {
                tileSelected = map[pos.y + 1][pos.x];
            }

            // Ticket #323: si hay un hechizo o item de rango seleccionado,
            // NO mandamos el packet `click` — sino el server abre
            // modales (Banco / Comerciante / Sastre / Ferry) que tapan
            // el canvas y el user no ve el FX del spell aunque sí se
            // castee. Estándar AO: con crosshair activo, el click es
            // exclusivamente acción ofensiva.
            if (!this.config.hechizoIdSpell && !this.config.itemSelected) {
                this.pkg.setPackageID(this.pkg.serverPacketID.click);
                this.pkg.writeByte(pos.x);
                this.pkg.writeByte(pos.y);
                this.config.ws.send(this.pkg.dataSend());
            }

            if (this.config.hechizoIdSpell) {
                this.react.setState({ crosshair: false });

                // Resolver el hechizo SELECCIONADO escaneando user.spells por
                // idSpell (no por slot). Hace el cast inmune a:
                //   - reordenamiento client-side de la lista de hechizos
                //   - desync cliente/server del idPos
                //   - olvido/aprendizaje de hechizos durante la selección
                let spell = null;
                const spells = (this.user && this.user.spells) || {};
                for (const k in spells) {
                    if (
                        spells[k] &&
                        spells[k].idSpell === this.config.hechizoIdSpell
                    ) {
                        spell = spells[k];
                        break;
                    }
                }

                if (!spell) {
                    // El char no conoce este hechizo (puede haberlo olvidado
                    // entre la selección y el click, desync server↔cliente
                    // tras reconnect, o packet quitarSpell mid-cast).
                    // CRIT-65: las otras ramas (mana insuficiente / cooldown)
                    // dan feedback — esta también para que el user no quede
                    // confundido pensando que el click "no hizo nada".
                    this.game.writeConsole(
                        "Hechizo no encontrado.",
                        "gray"
                    );
                    this.config.hechizoIdSpell = 0;
                } else if (
                    +Date.now() - this.config.timeSpellStart >
                    this.config.intervalSpell
                ) {
                    // v2026-06-26: gate de Magia removido — la feature
                    // necesita pulirse (skills server-side, balance, etc.)
                    // antes de gatear casts de verdad. Re-enable cuando
                    // esté el sistema completo.

                    if (this.user.mana >= (spell.manaRequired || 0)) {
                        this.config.timeSpellStart = +Date.now();

                        this.pkg.setPackageID(this.pkg.serverPacketID.attackSpell);
                        // Mandamos idSpell canónico (NO el slot). El server
                        // valida que el user lo conozca escaneando user.spells.
                        this.pkg.writeByte(this.config.hechizoIdSpell);
                        this.pkg.writeByte(pos.x);
                        this.pkg.writeByte(pos.y);
                        this.config.ws.send(this.pkg.dataSend());
                        // v2026-06-26: subida natural de Magia al castear.
                        // Fórmula AO classic VB6 — ver Modulo_UsUaRiOs.bas
                        // (SubirSkill). Probabilística + cap por nivel.
                        this._tryRiseMagia();
                    } else {
                        this.game.writeConsole("No tiene suficiente mana.", "gray");
                    }

                    this.config.hechizoIdSpell = 0;
                } else {
                    this.game.writeConsole(
                        "Aún no puedes realizar esa acción.",
                        "gray"
                    );
                }
            }

            if (this.config.itemSelected) {
                this.react.setState({ crosshair: false });

                if (
                    +Date.now() - this.config.timeRangeStart >
                    this.config.rangeInterval
                ) {
                    this.config.timeRangeStart = +Date.now();

                    this.pkg.setPackageID(this.pkg.serverPacketID.attackRange);
                    this.pkg.writeByte(pos.x);
                    this.pkg.writeByte(pos.y);
                    this.config.ws.send(this.pkg.dataSend());

                    this.config.itemSelected = 0;
                } else {
                    this.game.writeConsole(
                        "Aún no puedes realizar esa acción.",
                        "gray"
                    );
                }
            }
        } catch (err) {
            dumpError(err);
        }
    };

    setPersonaje = personaje => {
        // UX 2026-07-07: preservar el estado de animación/movimiento en
        // curso cuando el char YA existe. Sin esto, un sendCharacter
        // (re-broadcast completo por cambio de color/clan/faction — ej.
        // equipar la Vestimenta de GM, que togglea el tag <AOW Staff>)
        // que llega mientras el char está caminando lo RE-CREA desde cero:
        // salta a la pos del packet, corta la interpolación del paso, y el
        // nombre/silueta parpadea. Se notaba al spamear equip/desequip en
        // movimiento. Preservando estos campos, el packet actualiza
        // nombre/color/clan/sprite sin resetear la animación.
        const prev = this.personajes[personaje.id];
        if (prev) {
            personaje.moveOffsetX = prev.moveOffsetX;
            personaje.moveOffsetY = prev.moveOffsetY;
            personaje.scrollDirectionX = prev.scrollDirectionX;
            personaje.scrollDirectionY = prev.scrollDirectionY;
            personaje.moving = prev.moving;
            personaje.frameCounter = prev.frameCounter;
            personaje.frameCounterWeapon = prev.frameCounterWeapon;
            personaje.frameCounterShield = prev.frameCounterShield;
            personaje.frameFxCounter = prev.frameFxCounter;
        }
        this.personajes[personaje.id] = personaje;
    };

    deleteAllPersonajes = () => {
        this.personajes = {};
    };

    setAttrPersonaje = (idPersonaje, attr, data) => {
        if (this.personajes[idPersonaje]) {
            this.personajes[idPersonaje][attr] = data;
        }
    };

    deletePersonaje = idPersonaje => {
        if (this.personajes[idPersonaje]) {
            this.inits.mapData[this.config.mapNumber][
                this.personajes[idPersonaje].pos.y
            ][this.personajes[idPersonaje].pos.x].id = 0;

            delete this.personajes[idPersonaje];

            if (this.config.dialogs[idPersonaje]) {
                delete this.config.dialogs[idPersonaje];
            }
        }
    };

    actPositionServer = (pos, heading) => {
        try {
            const map = this.inits.mapData[this.config.mapNumber];
            const me = this.personajes[this.user.id];
            if (!map || !me) return;

            // Ticket #287: clear de moveOffsetX/Y del sprite del char.
            // Si había una animación de movimiento en curso (pixel offsets
            // no-cero) al recibir un snap del server, el sprite quedaba
            // fuera de centro hasta el siguiente movimiento manual del
            // user. Limpiando los offsets fuerza al sprite a pintarse en
            // el tile actual sin desplazamiento. Esto también arregla el
            // auto-resync sin que el user tenga que apretar P.
            me.moveOffsetX = 0;
            me.moveOffsetY = 0;

            if (this.user.pos.x != pos.x || this.user.pos.y != pos.y) {
                this.user.pos.x = pos.x;
                this.user.pos.y = pos.y;

                if (map[me.pos.y] && map[me.pos.y][me.pos.x]) {
                    map[me.pos.y][me.pos.x].id = 0;
                }

                me.pos.x = pos.x;
                me.pos.y = pos.y;
                me.heading = heading;

                if (map[me.pos.y] && map[me.pos.y][me.pos.x]) {
                    map[me.pos.y][me.pos.x].id = this.user.id;
                }

                this.renderBackground(0, 0);
            }
        } catch (err) {
            dumpError(err);
        }
    };

    inmovilizado = pos => {
        try {
            const map = this.inits.mapData[this.config.mapNumber];
            const me = this.personajes[this.user.id];
            if (!map || !me) return;

            if (this.user.pos.x != pos.x || this.user.pos.y != pos.y) {
                this.user.pos.x = pos.x;
                this.user.pos.y = pos.y;

                if (map[me.pos.y] && map[me.pos.y][me.pos.x]) {
                    map[me.pos.y][me.pos.x].id = 0;
                }

                me.pos.x = pos.x;
                me.pos.y = pos.y;

                if (map[me.pos.y] && map[me.pos.y][me.pos.x]) {
                    map[me.pos.y][me.pos.x].id = this.user.id;
                }

                this.renderBackground(0, 0);
            }
        } catch (err) {
            dumpError(err);
        }
    };

    hit = () => {
        try {
            const me = this.personajes[this.user.id];
            const map = this.inits.mapData[this.config.mapNumber];
            if (!me || !map) return;

            const heading = me.heading;
            const x = me.pos.x;
            const y = me.pos.y;
            let valido = false;

            const tileAt = (ty, tx) => map[ty] && map[ty][tx];

            switch (heading) {
                case 1: {
                    const t = tileAt(y - 1, x);
                    if (t && t.id !== 0) valido = true;
                    break;
                }
                case 2: {
                    const t = tileAt(y + 1, x);
                    if (t && t.id !== 0) valido = true;
                    break;
                }
                case 3: {
                    const t = tileAt(y, x + 1);
                    if (t && t.id !== 0) valido = true;
                    break;
                }
                case 4: {
                    const t = tileAt(y, x - 1);
                    if (t && t.id !== 0) valido = true;
                    break;
                }
                default:
                    console.log("Invalid heading");
                    break;
            }

            if (valido) {
                this.config.timeHitStart = +Date.now();

                this.pkg.setPackageID(this.pkg.serverPacketID.attackMele);
                this.config.ws.send(this.pkg.dataSend());
            }
        } catch (err) {
            dumpError(err);
        }
    };

    loop = () => {
        //stats.begin();

        // Perf 2026-07-07: si destroy() corrió (logout/unmount), NO
        // re-agendar. Sin este guard, cada reconexión dejaba un loop
        // fantasma renderizando el mundo completo a canvases desmontados
        // — 2× el CPU de render por cada ciclo de reconnect acumulado.
        if (this._destroyed) return;

        // Skip frame si el tab está hidden. Chrome throttea RAF a ~1Hz
        // en background → al volver al tab el primer delta sería ~1000ms,
        // multiplicaba scrolls/frameCounters por valores enormes y se
        // veían glitches visuales (sprites flickeando, dialogs salteados).
        // Aquí no hacemos trabajo; el visibilitychange en el constructor
        // resetea lastDelta así la resumida es suave.
        if (!document.hidden && this.user.id) {
            const now = this.timestamp();
            // Cap del delta a 50ms (~3 frames worth). Defense in depth para
            // cualquier otro motivo que cause un long-gap (devtools paused,
            // alert dialog, etc) — sin esto, un solo frame puede mover al
            // char varios tiles de golpe o saltar animaciones.
            this.delta = Math.min(now - this.lastDelta, 50);
            this.lastDelta = now;

            this.check();
            this.render();
        }

        //stats.end();

        this._rafId = requestAnimationFrame(this.loop);
    };

    check = () => {
        const { showInputText } = this.react.state;

        if (
            !this.user.moving &&
            this.user.pos.x > 0 &&
            this.user.pos.y > 0 &&
            !showInputText
        ) {
            // ── Movimiento: la tecla MÁS RECIENTE manda (ticket #210) ──
            // Antes: cadena `if/else if` con orden hardcoded left→right→up
            // →down, holdear A bloqueaba W. Ahora walk del keyOrder de
            // fin (más reciente) a inicio. Primer match con una de las 4
            // direcciones gana y dispara moveTo. Si soltás esa tecla, el
            // próximo frame fallback automático a la siguiente del stack
            // (la previa que sigue holded). Da control responsive sin
            // pisado de teclas — equivalente al AO clásico.
            //
            // Perf: keyOrder.length ≤ 5 (movement+usar), bucle O(N) sobre
            // N≤5 → O(1) efectivo. Sin renders extra de React. CERO
            // impacto en el tick del juego.
            const KC = this.react.state.keyCodeDefault;
            const DIR = this.config.direcciones;
            const NKC = this.config.nameKeyCode;
            for (let i = this.keyOrder.length - 1; i >= 0; i--) {
                const kc = this.keyOrder[i];
                if (kc === KC[NKC.flechaIzquierda]) {
                    this.moveTo(this.user.id, DIR.left);
                    break;
                }
                if (kc === KC[NKC.flechaDerecha]) {
                    this.moveTo(this.user.id, DIR.right);
                    break;
                }
                if (kc === KC[NKC.flechaArriba]) {
                    this.moveTo(this.user.id, DIR.up);
                    break;
                }
                if (kc === KC[NKC.flechaAbajo]) {
                    this.moveTo(this.user.id, DIR.down);
                    break;
                }
                // Si encontramos una tecla movement, break (return arriba).
                // Si es otra tecla movement-tracked pero NO direccional
                // (ej. KC[4] = Usar), seguimos buscando — el stack puede
                // tener Usar arriba de un W, queremos W ganando.
            }
        }
    };

    clearRender = capa => {
        this.canvas[capa].ctx.clearRect(
            0,
            0,
            this.config.canvasSize.width,
            this.config.canvasSize.height
        );
    };

    showNextFrame = () => {
        try {
            if (this.user.moving) {
                if (this.user.addtoUserPos.x) {
                    // Ticket #238: usar this.scrollPixelsPerFrameX (era
                    // hardcoded 9, inconsistente con el resto del engine
                    // que ya usaba la variable). Ahora sí responde al
                    // bump del ticket.
                    this.offsetCounterX =
                        this.offsetCounterX -
                        this.scrollPixelsPerFrameX *
                            this.user.addtoUserPos.x *
                            this.timerTicksPerFrame;

                    if (
                        Math.abs(this.offsetCounterX) >=
                        Math.abs(32 * this.user.addtoUserPos.x)
                    ) {
                        this.offsetCounterX = 0;
                        this.user.addtoUserPos.x = 0;
                        this.user.moving = false;
                    }
                }

                if (this.user.addtoUserPos.y) {
                    this.offsetCounterY =
                        this.offsetCounterY -
                        this.scrollPixelsPerFrameY *
                            this.user.addtoUserPos.y *
                            this.timerTicksPerFrame;

                    if (
                        Math.abs(this.offsetCounterY) >=
                        Math.abs(32 * this.user.addtoUserPos.y)
                    ) {
                        this.offsetCounterY = 0;
                        this.user.addtoUserPos.y = 0;
                        this.user.moving = false;
                    }
                }

                this.renderBackground(this.offsetCounterX, this.offsetCounterY);
            }

            this.renderScreen(
                this.user.pos.x - this.user.addtoUserPos.x,
                this.user.pos.y - this.user.addtoUserPos.y,
                this.offsetCounterX,
                this.offsetCounterY
            );
        } catch (err) {
            dumpError(err);
        }
    };

    fps = () => {
        if (this.timestamp() - this.lFrameTimer > 1000) {
            this.FPS = this.framesPerSecCounter;
            this.framesPerSecCounter = 0;
            this.lFrameTimer = this.timestamp();
        }
    };

    render = () => {
        try {
            this.showNextFrame();

            this.fps();
            this.framesPerSecCounter = this.framesPerSecCounter + 1;
            this.timerTicksPerFrame = this.delta * this.engineBaseSpeed;
        } catch (err) {
            dumpError(err);
        }
    };

    // Ticket #225: scan one-shot del mapa para popular el cache de
    // posiciones de items. ~10000 cells × O(1) lookup ≈ 1-2ms. Solo se
    // ejecuta la primera vez que el cache se consulta para un mapa.
    _scanItemsForMap = mapNumber => {
        const positions = [];
        const mapGrid = this.inits.mapa[mapNumber];
        if (mapGrid) {
            for (let y = 1; y <= 100; y++) {
                const row = mapGrid[y];
                if (!row) continue;
                for (let x = 1; x <= 100; x++) {
                    const cell = row[x];
                    if (cell && cell.o) positions.push({ y, x });
                }
            }
        }
        this._itemPositionsByMap[mapNumber] = positions;
        return positions;
    };

    // Ticket #225: lookup del cache con scan lazy. Mantiene un Array de
    // {y, x} por mapa. updateItems itera SOLO estas posiciones (típico
    // 50-200 vs 1089 tiles del viewport) → ~5-10× menos lookups por frame
    // durante movimiento.
    _getItemPositions = mapNumber => {
        return (
            this._itemPositionsByMap[mapNumber] ||
            this._scanItemsForMap(mapNumber)
        );
    };

    // Llamado por messages.renderItem (server avisa que un item apareció
    // en el mapa). Si el cache ya existe, agregamos. Si no, el primer
    // _getItemPositions hará el scan completo y lo encontrará vía la
    // mutación previa de inits.mapa.
    _addItemPosition = (mapNumber, y, x) => {
        const positions = this._itemPositionsByMap[mapNumber];
        if (!positions) return;
        // Evitar duplicado si renderItem se dispara 2x para el mismo tile.
        for (let i = 0; i < positions.length; i++) {
            if (positions[i].y === y && positions[i].x === x) return;
        }
        positions.push({ y, x });
    };

    // Llamado por messages.deleteItem. Splice out por matching (y, x).
    _removeItemPosition = (mapNumber, y, x) => {
        const positions = this._itemPositionsByMap[mapNumber];
        if (!positions) return;
        for (let i = 0; i < positions.length; i++) {
            if (positions[i].y === y && positions[i].x === x) {
                positions.splice(i, 1);
                return;
            }
        }
    };

    // Ticket #237: ANTES dibujaba al canvas `.items` (z=1) que quedaba
    // debajo del `.foreground` (z=2) donde se pintan las animaciones de
    // fondo (fuego, agua). Resultado: items single-frame sobre tiles con
    // animación quedaban tapados visualmente.
    //
    // AHORA renderea al canvas `.foreground` y se llama desde renderScreen
    // DESPUÉS del fuego pero ANTES de los chars — orden correcto:
    //   fondo (animado) → item → char → techos.
    //
    // El canvas `.items` queda sin uso. clearRender("foreground") lo hace
    // renderScreen al tope, no acá.
    updateItems = (pixelOffsetX, pixelOffsetY) => {
        const tileX = this.user.pos.x - this.user.addtoUserPos.x;
        const tileY = this.user.pos.y - this.user.addtoUserPos.y;

        let minXOffset = 0;
        let minYOffset = 0;

        // Window "long" (-(r-1)..+r). Derivado de viewportRadius en
        // config.js para que un futuro cambio del viewport visible
        // (canvasSize → más tiles) ajuste todo proporcionalmente.
        const r = this.config.viewportRadius;
        const screenminY = tileY - (r - 1);
        const screenmaxY = tileY + r;
        const screenminX = tileX - (r - 1);
        const screenmaxX = tileX + r;

        let minY = screenminY - this.config.TileBufferSize + 2;
        let maxY = screenmaxY + this.config.TileBufferSize;
        let minX = screenminX - this.config.TileBufferSize + 2;
        let maxX = screenmaxX + this.config.TileBufferSize;

        if (minY < this.config.YMinMapSize) {
            minYOffset = this.config.YMinMapSize - minY;
            minY = this.config.YMinMapSize;
        }

        if (maxY > this.config.YMaxMapSize) {
            maxY = this.config.YMaxMapSize;
        }

        if (minX < this.config.XMinMapSize) {
            minXOffset = this.config.XMinMapSize - minX;
            minX = this.config.XMinMapSize;
        }

        if (maxX > this.config.XMaxMapSize) {
            maxX = this.config.XMaxMapSize;
        }

        // Ticket #225: iteramos solo las posiciones cacheadas de items
        // (~50-200 en mapas típicos), filtrando por el viewport calculado
        // arriba — en vez de los ~1089 tile-lookups del loop anidado
        // original. La fórmula de ScreenY/ScreenX por tile es la misma
        // que tenía el loop:
        //   ScreenY(y) = minYOffset - TileBufferSize + (y - minY)
        //   ScreenX(x) = minXOffset - TileBufferSize + (x - minX)
        const mapNumber = this.config.mapNumber;
        const positions = this._getItemPositions(mapNumber);
        const mapData = this.inits.mapa[mapNumber];
        if (!mapData) return;

        const screenYBase = minYOffset - this.config.TileBufferSize;
        const screenXBase = minXOffset - this.config.TileBufferSize;

        for (let i = 0; i < positions.length; i++) {
            const { y, x } = positions[i];
            // Mismo rango del loop original (y < maxY, x < maxX).
            if (y < minY || y >= maxY || x < minX || x >= maxX) continue;

            const row = mapData[y];
            if (!row) continue;
            const cell = row[x];
            if (!cell) continue;
            const objInfo = cell.o;
            if (!objInfo) continue;

            const objCatalog = this.inits.objs[objInfo.oi];
            if (!objCatalog) continue;
            let grhObj = this.inits.graphics[objCatalog.grhIndex];
            if (!grhObj || grhObj.numFrames !== 1) continue;

            const ScreenY = screenYBase + (y - minY);
            const ScreenX = screenXBase + (x - minX);

            let PixelOffsetXTemp = ScreenX * 32 + pixelOffsetX;
            let PixelOffsetYTemp = ScreenY * 32 + pixelOffsetY;

            const CurrentGrhIndex = grhObj.frames[1];
            grhObj = this.inits.graphics[CurrentGrhIndex];

            PixelOffsetXTemp -= Math.floor((grhObj.width * 16) / 32) - 48;
            PixelOffsetYTemp -= Math.floor((grhObj.height * 16) / 16) - 64;

            // Ticket #237: target = foreground (no más .items canvas).
            this.drawGrhCapa(
                "foreground",
                grhObj,
                PixelOffsetXTemp,
                PixelOffsetYTemp
            );
        }
    };

    renderBackground = (pixelOffsetX, pixelOffsetY) => {
        this.clearRender("background");
        this.clearRender("techos");

        const tileX = this.user.pos.x - this.user.addtoUserPos.x;
        const tileY = this.user.pos.y - this.user.addtoUserPos.y;

        let ScreenX = 0;
        let ScreenY = 0;

        // Window "long" — ver renderItems para detalles del derivado.
        const r = this.config.viewportRadius;
        const screenminY = tileY - (r - 1);
        const screenmaxY = tileY + r;
        const screenminX = tileX - (r - 1);
        const screenmaxX = tileX + r;

        let minY = screenminY - this.config.TileBufferSize + 2;
        let maxY = screenmaxY + this.config.TileBufferSize;
        let minX = screenminX - this.config.TileBufferSize + 2;
        let maxX = screenmaxX + this.config.TileBufferSize;

        if (minY < this.config.YMinMapSize) {
            minY = this.config.YMinMapSize;
        }

        if (maxY > this.config.YMaxMapSize) {
            maxY = this.config.YMaxMapSize;
        }

        if (minX < this.config.XMinMapSize) {
            minX = this.config.XMinMapSize;
        }

        if (maxX > this.config.XMaxMapSize) {
            maxX = this.config.XMaxMapSize;
        }

        for (let y = screenminY; y < screenmaxY; y++) {
            for (let x = screenminX; x < screenmaxX; x++) {
                if (
                    x > 0 &&
                    x <= this.config.XMaxMapSize &&
                    y > 0 &&
                    y <= this.config.YMaxMapSize
                ) {
                    const tempPixelOffsetX = (ScreenX - 1) * 32 + pixelOffsetX;
                    const tempPixelOffsetY = (ScreenY - 1) * 32 + pixelOffsetY;

                    const cell = this.inits.mapa[this.config.mapNumber][y][x];
                    const grhCapa1 = this.inits.graphics[cell.g[1]];

                    if (grhCapa1 && grhCapa1.numFrames === 1) {
                        this.drawGrhCapa(
                            "background",
                            grhCapa1,
                            tempPixelOffsetX,
                            tempPixelOffsetY
                        );
                    }

                    // Perf 2026-07-07: capa2 (decorados) migrada acá desde
                    // renderScreen — antes se blitteaba al foreground CADA
                    // frame (hasta ~529 drawImage/frame parado, 2-8ms en
                    // zonas densas). El background solo se redibuja al
                    // moverse.
                    //
                    // Fix 2026-07-08 (orillas): SOLO dibujamos capa2 acá si
                    // la capa1 de ESTE tile es estática. Si capa1 es animada
                    // (agua/lava), su animación se pinta en renderScreen
                    // (canvas foreground, z=2) — que está ENCIMA de este
                    // background (z=0). Dibujar la orilla (capa2) acá la
                    // dejaría tapada por el agua. En ese caso la capa2 se
                    // difiere a renderScreen para quedar sobre la animación.
                    // Son ~34k tiles de costa: NO era "combinación rarísima".
                    const capa1Animada = grhCapa1 && grhCapa1.numFrames > 1;
                    const grhCapa2 = this.inits.graphics[cell.g[2]];
                    if (grhCapa2 && grhCapa2.numFrames && !capa1Animada) {
                        this.drawGrhCapa(
                            "background",
                            grhCapa2,
                            tempPixelOffsetX,
                            tempPixelOffsetY
                        );
                    }
                } else {
                    let tmpX = x;
                    let tmpY = y;

                    const tempPixelOffsetX = (ScreenX - 1) * 32 + pixelOffsetX;
                    const tempPixelOffsetY = (ScreenY - 1) * 32 + pixelOffsetY;

                    if (y > this.config.YMaxMapSize) {
                        tmpY = this.config.YMaxMapSize;
                    }
                    if (y <= 0) {
                        tmpY = 1;
                    }

                    if (x > this.config.XMaxMapSize) {
                        tmpX = this.config.XMaxMapSize;
                    }
                    if (x <= 0) {
                        tmpX = 1;
                    }

                    const grhCapa1 = this.inits.graphics[
                        this.inits.mapa[this.config.mapNumber][tmpY][tmpX].g[1]
                    ];

                    if (grhCapa1 && grhCapa1.numFrames === 1) {
                        this.drawGrhCapa(
                            "background",
                            grhCapa1,
                            tempPixelOffsetX,
                            tempPixelOffsetY
                        );
                    }
                }

                ScreenX = ScreenX + 1;
            }

            ScreenX = 0;
            ScreenY = ScreenY + 1;
        }

        // Ticket #237: updateItems se movió a renderScreen (capa foreground)
        // para quedar arriba del fuego animado.

        this.config.actTechos = true;
    };

    renderScreen = (tileX, tileY, pixelOffsetX, pixelOffsetY) => {
        //try {
        this.clearRender("foreground");
        this.clearRender("textos");

        // Cache de Date.now() por frame — usado por renderNpcHealthBar +
        // renderNpcStatusBar para checks de visibilidad. Antes cada NPC
        // visible hacía su propio Date.now() (~0.05ms cada uno) × N
        // bichos × 60fps = ~60ms/s en zonas pobladas. Una sola llamada
        // por frame elimina ese overhead.
        this._frameNow = Date.now();

        let PixelOffsetXTemp = 0;
        let PixelOffsetYTemp = 0;

        let minXOffset = 0;
        let minYOffset = 0;

        let ScreenX = 0;
        let ScreenY = 0;

        // Dos windows staggered 1 tile entre sí para el blending de capas
        // foreground / techos. La "short" cubre -r..+(r-1), la "long" cubre
        // -(r-1)..+r. r viene de config.viewportRadius.
        const r = this.config.viewportRadius;
        const screenminY = tileY - r;
        const screenmaxY = tileY + (r - 1);
        const screenminX = tileX - r;
        const screenmaxX = tileX + (r - 1);

        const screenminY2 = tileY - (r - 1);
        const screenmaxY2 = tileY + r;
        const screenminX2 = tileX - (r - 1);
        const screenmaxX2 = tileX + r;

        let minY = screenminY - this.config.TileBufferSize + 3;
        let maxY = screenmaxY + this.config.TileBufferSize;
        let minX = screenminX - this.config.TileBufferSize + 3;
        let maxX = screenmaxX + this.config.TileBufferSize;

        if (minY < this.config.YMinMapSize) {
            minYOffset = this.config.YMinMapSize - minY;
            minY = this.config.YMinMapSize;
        }

        if (maxY > this.config.YMaxMapSize) {
            maxY = this.config.YMaxMapSize;
        }

        if (minX < this.config.XMinMapSize) {
            minXOffset = this.config.XMinMapSize - minX;
            minX = this.config.XMinMapSize;
        }

        if (maxX > this.config.XMaxMapSize) {
            maxX = this.config.XMaxMapSize;
        }

        // Perf 2026-07-07:
        //   - capa2 (decorados estáticos) ya NO se dibuja acá — migró a
        //     renderBackground (solo redibuja al moverse). Este loop solo
        //     anima capa1 (agua/fuego/lava).
        //   - lookups de mapa/graphics hoisteados fuera del loop (antes
        //     se re-indexaba this.inits.mapa[mapNumber][y][x] por tile).
        const mapaGrid = this.inits.mapa[this.config.mapNumber];
        const mapDataGrid = this.inits.mapData[this.config.mapNumber];
        const graphicsCat = this.inits.graphics;
        const objsCat = this.inits.objs;
        const xMaxMap = this.config.XMaxMapSize;
        const yMaxMap = this.config.YMaxMapSize;

        for (let y = screenminY2; y < screenmaxY2; y++) {
            const mapaRow = y > 0 && y <= yMaxMap ? mapaGrid[y] : null;
            for (let x = screenminX2; x < screenmaxX2; x++) {
                if (mapaRow && x > 0 && x <= xMaxMap) {
                    let grhCapa1 = graphicsCat[mapaRow[x].g[1]];

                    if (grhCapa1 && grhCapa1.numFrames > 1) {
                        const tempPixelOffsetX = (ScreenX - 1) * 32 + pixelOffsetX;
                        const tempPixelOffsetY = (ScreenY - 1) * 32 + pixelOffsetY;

                        grhCapa1.frameCounter += this.delta / grhCapa1.speed;

                        if (
                            Math.ceil(grhCapa1.frameCounter) >
                            grhCapa1.numFrames
                        ) {
                            grhCapa1.frameCounter = 0;
                            grhCapa1.frameCounter +=
                                this.delta / grhCapa1.speed;
                        }

                        const CurrentGrhIndex =
                            grhCapa1.frames[Math.ceil(grhCapa1.frameCounter)];

                        grhCapa1 = graphicsCat[CurrentGrhIndex];

                        this.drawGrhCapa(
                            "foreground",
                            grhCapa1,
                            tempPixelOffsetX,
                            tempPixelOffsetY
                        );

                        // Fix 2026-07-08 (orillas): la capa2 (decal de orilla)
                        // sobre agua animada NO se dibujó en renderBackground
                        // (lo saltea justo cuando capa1 es animada). La
                        // pintamos acá, ENCIMA del agua, para el z-order
                        // correcto — sino el agua tapaba el borde de la costa.
                        const grhCapa2 = graphicsCat[mapaRow[x].g[2]];
                        if (grhCapa2 && grhCapa2.numFrames) {
                            this.drawGrhCapa(
                                "foreground",
                                grhCapa2,
                                tempPixelOffsetX,
                                tempPixelOffsetY
                            );
                        }
                    }
                }
                ScreenX = ScreenX + 1;
            }

            ScreenX = 0;
            ScreenY = ScreenY + 1;
        }

        // Ticket #237: render de items single-frame al foreground, AQUÍ —
        // entre fuego/g[2] (Loop 1) y animated items + chars + g[3] (Loops 2+3).
        // Resultado: items visibles sobre fuego/agua/lava, pero tapados por
        // chars cuando sus sprites se superponen (la cabeza del char cubre
        // items que están un tile al norte) y por árboles/decoraciones g[3].
        this.updateItems(pixelOffsetX, pixelOffsetY);

        ScreenY = minYOffset - this.config.TileBufferSize;

        for (let y = minY; y < maxY; y++) {
            ScreenX = minXOffset - this.config.TileBufferSize;
            const mapaRow2 = mapaGrid[y];

            for (let x = minX; x < maxX; x++) {
                PixelOffsetXTemp = ScreenX * 32 + pixelOffsetX;
                PixelOffsetYTemp = ScreenY * 32 + pixelOffsetY;

                const objInfo = mapaRow2[x].o;

                if (objInfo) {
                    // Teleport Portals (378-381) se renderean en una pasada
                    // POSTERIOR (después de capa 3) para que no queden
                    // tapados por piedras/árboles/decoraciones del foreground.
                    // Skip acá; la pasada de portales hace el dibujo.
                    if (objInfo.oi >= 378 && objInfo.oi <= 381) {
                        ScreenX = ScreenX + 1;
                        continue;
                    }
                    // Ticket #298 fix: guard contra item sin catalog. Si el
                    // server manda un item dropeado cuyo idItem no está en
                    // `this.inits.objs` (cliente con catalog stale por cache,
                    // o item recién creado por GM antes del fresh-load del
                    // cliente), el render lanzaba TypeError al leer .grhIndex
                    // de undefined → el try/catch del frame absorbía pero
                    // abortaba el resto del render → "todo desaparece hasta
                    // recoger el item". Mismo guard que ya existe en
                    // `scanCachedItems` (línea ~851) — replicar acá.
                    const objCatalog = objsCat[objInfo.oi];
                    if (!objCatalog) {
                        ScreenX = ScreenX + 1;
                        continue;
                    }
                    let grhObj = graphicsCat[objCatalog.grhIndex];

                    if (grhObj && grhObj.numFrames > 1) {
                        grhObj.frameCounter += this.delta / grhObj.speed;

                        if (Math.ceil(grhObj.frameCounter) > grhObj.numFrames) {
                            grhObj.frameCounter = 0;
                            grhObj.frameCounter += this.delta / grhObj.speed;
                        }

                        const CurrentGrhIndex =
                            grhObj.frames[Math.ceil(grhObj.frameCounter)];

                        grhObj = this.inits.graphics[CurrentGrhIndex];

                        PixelOffsetXTemp -=
                            Math.floor((grhObj.width * 16) / 32) - 48;
                        PixelOffsetYTemp -=
                            Math.floor((grhObj.height * 16) / 16) - 64;

                        // Tinte client-side por objIndex — diferenciadores
                        // visuales del Teleport Portal canónico (obj 378
                        // rosa-violeta sin filter). Mismo grhIndex 669 para
                        // todos, recolor via hue-rotate distinto en cada uno.
                        //   - 379 "Efímero"     → azul puro
                        //   - 380 "Sueño"       → verde
                        //   - 381 "Permanente"  → rojo
                        let tint = null;
                        if (objInfo.oi === 379) tint = "blue";
                        else if (objInfo.oi === 380) tint = "green";
                        else if (objInfo.oi === 381) tint = "red";
                        this.drawGrhCapa(
                            "foreground",
                            grhObj,
                            PixelOffsetXTemp,
                            PixelOffsetYTemp,
                            null,
                            tint
                        );
                    }
                }

                ScreenX = ScreenX + 1;
            }

            ScreenY = ScreenY + 1;
        }

        // Loop 3 unificado por Y creciente. Orden per-tile:
        //   1. Capa 3 (decoración alta — árboles, piedras, edificios).
        //   2. Teleport Portal (si el tile tiene objInfo 378-381) —
        //      por encima de la capa 3 del mismo tile para que no quede
        //      tapado por piedras adyacentes (Y-ordering por tiles).
        //   3. Char (si hay) — por encima del portal del mismo tile para
        //      que el sprite del char se vea cuando pisa el portal.
        // Mantener Y creciente preserva la inmersión "árbol al sur tapa
        // char al norte" (porque tiles del sur se dibujan después).
        ScreenY = minYOffset - this.config.TileBufferSize;

        for (let y = minY; y < maxY; y++) {
            ScreenX = minXOffset - this.config.TileBufferSize;
            const mapaRow3 = mapaGrid[y];
            const mapDataRow3 = mapDataGrid[y];

            for (let x = minX; x < maxX; x++) {
                PixelOffsetXTemp = (ScreenX + 0) * 32 + pixelOffsetX;
                PixelOffsetYTemp = (ScreenY + 0) * 32 + pixelOffsetY;

                // Perf 2026-07-07: la celda se lee UNA vez por tile (antes
                // se re-indexaba mapa[mapNumber][y][x] para g[3] y de nuevo
                // para .o, y mapData[mapNumber][y][x] dos veces más abajo).
                const cell = mapaRow3[x];

                let grhCapa3 = graphicsCat[cell.g[3]];

                // === 1. CAPA 3 (decoraciones) ===
                if (grhCapa3 && grhCapa3.numFrames) {
                    const CurrentGrhIndex = grhCapa3.frames[1];

                    grhCapa3 = graphicsCat[CurrentGrhIndex];

                    let c3X = PixelOffsetXTemp;
                    let c3Y = PixelOffsetYTemp;
                    c3X -= Math.floor((grhCapa3.width * 16) / 32) - 48;
                    c3Y -= Math.floor((grhCapa3.height * 16) / 16) - 64;

                    this.drawGrhCapa(
                        "foreground",
                        grhCapa3,
                        c3X,
                        c3Y
                    );
                }

                // === 2. PORTAL (objIndex 378-381) por encima de capa 3 ===
                const objInfoPortal = cell && cell.o;
                if (
                    objInfoPortal &&
                    objInfoPortal.oi >= 378 &&
                    objInfoPortal.oi <= 381
                ) {
                    // Ticket #298 fix: mismo guard que en items normales.
                    // Defensivo aunque portales 378-381 son canónicos del
                    // JSON inicial — si el catalog cliente no los cargó por
                    // razones de cache, no crasheamos el frame.
                    // Bugfix 2026-07-07: antes era `continue` — salteaba el
                    // ScreenX++ del final del loop Y el render del char del
                    // tile, desalineando toda la fila. Guard sin continue.
                    const portalCatalog = objsCat[objInfoPortal.oi];
                    let grhPortal = portalCatalog
                        ? graphicsCat[portalCatalog.grhIndex]
                        : null;
                    if (grhPortal && grhPortal.numFrames > 1) {
                        grhPortal.frameCounter += this.delta / grhPortal.speed;
                        if (
                            Math.ceil(grhPortal.frameCounter) > grhPortal.numFrames
                        ) {
                            grhPortal.frameCounter = 0;
                            grhPortal.frameCounter += this.delta / grhPortal.speed;
                        }
                        const CurrentGrhIndex =
                            grhPortal.frames[Math.ceil(grhPortal.frameCounter)];
                        grhPortal = this.inits.graphics[CurrentGrhIndex];

                        let pX = PixelOffsetXTemp;
                        let pY = PixelOffsetYTemp;
                        pX -= Math.floor((grhPortal.width * 16) / 32) - 48;
                        pY -= Math.floor((grhPortal.height * 16) / 16) - 64;

                        let tint = null;
                        if (objInfoPortal.oi === 379) tint = "blue";
                        else if (objInfoPortal.oi === 380) tint = "green";
                        else if (objInfoPortal.oi === 381) tint = "red";
                        this.drawGrhCapa(
                            "foreground",
                            grhPortal,
                            pX,
                            pY,
                            null,
                            tint
                        );
                    }
                }

                // === 3. CHAR (encima de portal y capa 3 del mismo tile) ===
                if (mapDataRow3 && x >= 0 && x <= xMaxMap) {
                    const tileId = mapDataRow3[x].id;
                    if (tileId) {
                        const personaje = this.personajes[tileId];

                        if (personaje) {
                            this.charRender(
                                personaje,
                                (ScreenX + 1) * 32 + pixelOffsetX,
                                (ScreenY + 1) * 32 + pixelOffsetY
                            );
                        }
                    }
                }

                ScreenX = ScreenX + 1;
            }

            ScreenY = ScreenY + 1;
        }

        if (this.config.actTechos) {
            ScreenY = minYOffset - this.config.TileBufferSize;
            for (let y = minY; y < maxY; y++) {
                ScreenX = minXOffset - this.config.TileBufferSize;
                const mapaRow4 = mapaGrid[y];

                for (let x = minX; x < maxX; x++) {
                    let PixelOffsetXTemp = ScreenX * 32 + pixelOffsetX;
                    let PixelOffsetYTemp = ScreenY * 32 + pixelOffsetY;

                    let grhCapa4 = graphicsCat[mapaRow4[x].g[4]];

                    if (grhCapa4 && grhCapa4.numFrames) {
                        var CurrentGrhIndex = grhCapa4.frames[1];

                        grhCapa4 = this.inits.graphics[CurrentGrhIndex];
                        PixelOffsetXTemp -=
                            Math.floor((grhCapa4.width * 16) / 32) - 48;
                        PixelOffsetYTemp -=
                            Math.floor((grhCapa4.height * 16) / 16) - 64;

                        this.drawGrhCapa(
                            "techos",
                            grhCapa4,
                            PixelOffsetXTemp,
                            PixelOffsetYTemp,
                            true
                        );
                    }

                    ScreenX = ScreenX + 1;
                }

                ScreenY = ScreenY + 1;
            }

            this.config.actTechos = false;
        }

        this.cargarDescCliente();

        // HUD text: ping/FPS y Seguro se redibujan en cada frame porque
        // la layer del canvas se clearea, PERO el contenido de los strings
        // solo cambia a ~1Hz (FPS se updatea cada 1s, ping cuando llega
        // pong packet). Cacheamos los strings + el width del ping para
        // posicionar FPS después sin medir cada frame.
        // Ping en celeste argentina, FPS en cream — los dos colores
        // refuerzan identidad de marca (paleta del logo del navbar).
        if (
            this._lastPing !== this.ping ||
            this._lastFPS !== this.FPS
        ) {
            this._pingText = "Ping (ms): " + this.ping;
            this._fpsText = "FPS: " + this.FPS;
            this._lastPing = this.ping;
            this._lastFPS = this.FPS;
            // Cache width del ping para colocar FPS después sin medir
            // cada frame. Mismo font que vamos a usar en el render.
            const ctx = this.canvas.textos.ctx;
            ctx.font = "bold 12px Tahoma";
            this._pingTextWidth = ctx.measureText(this._pingText).width;
        }
        this.renderText(this._pingText, 10, 20, "#74acdf", true, 12, "Tahoma");
        this.renderText(
            this._fpsText,
            10 + (this._pingTextWidth || 0) + 12,
            20,
            "#fff8e8",
            true,
            12,
            "Tahoma"
        );

        // Seguro: el string es 1 de 2 valores fijos — cacheamos directo
        // junto con el color. (toggling low-frequency: tecla L del user,
        // no per-frame). Verde = activado (safe, no atacás ciudadanos);
        // rojo = desactivado (PK mode, podés volverte criminal al atacar).
        if (this._lastSeguro !== this.config.seguroActivado) {
            this._seguroText = this.config.seguroActivado
                ? "Seguro Activado"
                : "Seguro Desactivado";
            this._seguroColor = this.config.seguroActivado
                ? "#4caf50"
                : "#e53935";
            this._lastSeguro = this.config.seguroActivado;
        }
        this.renderText(
            this._seguroText,
            10,
            35,
            this._seguroColor,
            true,
            12,
            "Tahoma"
        );

        // Pesca (#325) / Minería (#326) / Talado (#327): aviso de trabajo
        // activo debajo de "Seguro" mientras el server confirma estado.
        // Rojo. Mutuamente excluyentes (misma tool slot) → else-if chain.
        if (this.pescando) {
            this.renderText("Pescando...", 10, 50, "#e53935", true, 12, "Tahoma");
        } else if (this.minando) {
            this.renderText("Minando...", 10, 50, "#e53935", true, 12, "Tahoma");
        } else if (this.talando) {
            this.renderText("Talando...", 10, 50, "#e53935", true, 12, "Tahoma");
        }

        // La Guarida del Bardo — mientras el toggle personal esté ON y
        // haya track sonando, mostramos "🎵 Radio · <título>" bajo el
        // work status. Perf 2026-07-07: el módulo se cachea al primer
        // uso (antes require() por frame) y el label solo se reconstruye
        // cuando el título cambió (antes slice+concat por frame).
        try {
            if (!this._radioEngine) {
                this._radioEngine = require("./radio").default;
            }
            const radioEngine = this._radioEngine;
            if (radioEngine.isEnabled() && radioEngine.hasTrack()) {
                const rawTitle = radioEngine.getCurrentTitle();
                if (rawTitle !== this._radioLastTitle) {
                    this._radioLastTitle = rawTitle;
                    if (rawTitle) {
                        const t = rawTitle.length > 42
                            ? rawTitle.slice(0, 42) + "…"
                            : rawTitle;
                        this._radioLabel = "🎵 Radio · " + t;
                    } else {
                        this._radioLabel = "🎵 Radio · sintonizando...";
                    }
                }
                this.renderText(this._radioLabel, 10, 65, "#e0a84e", true, 12, "Tahoma");
            }
        } catch (e) {}
    };

    cargarDescCliente = () => {
        if (this.config.descClient.length) {
            this.posYDescClient = 0;

            if (this.timeMoveDescClient > 500) {
                this.config.descClient = [];
            } else {
                this.timeMoveDescClient++;
            }
        }
    };

    // Filtros canvas por tint — definidos en un lookup para reutilizar
    // tanto en el path de filter directo (fallback) como en el cache builder.
    //   "blue"  → rosa(~hue 320°) → azul(~hue 240°): rotar -80°.
    //   "green" → rosa(~hue 320°) → verde(~hue 140°): rotar 180°.
    //   "red"   → rosa(~hue 320°) → rojo(~hue 0°):   rotar 50° + dark + sat.
    _filterForTint = (tint) => {
        if (tint === "blue") return "hue-rotate(-80deg) saturate(1.4) brightness(0.95)";
        if (tint === "green") return "hue-rotate(180deg) saturate(1.6) brightness(0.95)";
        if (tint === "red") return "hue-rotate(50deg) saturate(2) brightness(0.55) contrast(1.2)";
        // Ticket #298: "white" desatura el sprite y lo aclara — pensado para
        // el Dragón Blanco (body 41 del Dragón Rojo recolorizado). Si queda
        // muy lavado o muy contrastado, ajustar brightness/contrast acá.
        if (tint === "white") return "grayscale(1) brightness(1.8) contrast(0.7)";
        // Ticket #299: "black" oscurece el sprite manteniendo silueta —
        // contrapartida visual del Dragón Blanco. grayscale total + brightness
        // muy baja + contrast alto para preservar definición de bordes (sino
        // queda mancha negra plana). Si queda demasiado oscuro o demasiado
        // contrastado, ajustar brightness (0.3-0.5) o contrast (1.2-1.8).
        if (tint === "black") return "grayscale(1) brightness(0.35) contrast(1.5)";
        return "none";
    };

    // Devuelve un <canvas> off-screen con la región del sprite (grh.sX,
    // grh.sY, grh.width, grh.height) ya tintada. Lazy: build la primera
    // vez por key, después blit directo sin filter pass.
    _getTintedSprite = (grh, tint) => {
        const sourceImage = this.inits.preCacheGraphics[grh.numFile];
        if (!sourceImage) return null;
        const key = grh.numFile + ":" + grh.sX + ":" + grh.sY + ":" + grh.width + ":" + grh.height + ":" + tint;
        let off = this._tintedSpriteCache[key];
        if (off) return off;

        off = document.createElement("canvas");
        off.width = grh.width;
        off.height = grh.height;
        const offCtx = off.getContext("2d");
        offCtx.filter = this._filterForTint(tint);
        offCtx.drawImage(
            sourceImage,
            grh.sX, grh.sY, grh.width, grh.height,
            0, 0, grh.width, grh.height
        );
        offCtx.filter = "none";
        this._tintedSpriteCache[key] = off;
        return off;
    };

    drawGrhCapa = (capa, grh, x, y, alpha, tint) => {
        try {
            if (grh) {
                const image = this.inits.preCacheGraphics[grh.numFile];

                if (image) {
                    if (alpha) {
                        this.canvas[capa].ctx.globalAlpha = 0.5;
                    }

                    // Ticket #296: floor coords antes del drawImage cuando
                    // el flag pixelPerfect está ON. El offsetCounterX/Y se
                    // acumula como float (scrollPixels × baseSpeed × delta_ms
                    // ≈ 2.67px/frame a 60fps) y pasarlo a drawImage en
                    // sub-pixel deja gaps entre tiles adyacentes que aparecen
                    // como "líneas negras al caminar" (negras porque el ctx
                    // del .background es alpha:false → clearRect rellena
                    // en negro, no transparente).
                    //
                    // Toggle via Settings (Configuración → "Bordes nítidos
                    // de tiles"). Si está OFF preservamos las coords float
                    // originales — render "smooth" pero con gaps/halo
                    // visibles durante el movimiento.
                    const pp = this.pixelPerfect !== false;
                    const fx = pp ? (x | 0) : x;
                    const fy = pp ? (y | 0) : y;

                    // Fast path con tint: usar el sprite cacheado off-screen
                    // que ya tiene el tint aplicado. Evita el ctx.filter pass
                    // por frame — solo blit. ~10× más barato. Si el cache no
                    // está listo (sin sourceImage o build falló), cae al
                    // fallback de filter directo abajo.
                    const useTint = tint === "blue" || tint === "green" || tint === "red" || tint === "white" || tint === "black";
                    if (useTint) {
                        const tinted = this._getTintedSprite(grh, tint);
                        if (tinted) {
                            this.canvas[capa].ctx.drawImage(
                                tinted,
                                0, 0, grh.width, grh.height,
                                fx, fy, grh.width, grh.height
                            );
                            if (alpha) {
                                this.canvas[capa].ctx.globalAlpha = 1;
                            }
                            return;
                        }
                        // Fallback: setear filter directo (más caro pero
                        // garantiza el render aunque el cache haya fallado).
                        this.canvas[capa].ctx.filter = this._filterForTint(tint);
                    }

                    if (this.canvas[capa].ctx) {
                        this.canvas[capa].ctx.drawImage(
                            image,
                            grh.sX,
                            grh.sY,
                            grh.width,
                            grh.height,
                            fx,
                            fy,
                            grh.width,
                            grh.height
                        );
                    }
                    if (useTint) {
                        this.canvas[capa].ctx.filter = "none";
                    }
                    if (alpha) {
                        this.canvas[capa].ctx.globalAlpha = 1;
                    }
                } else {
                    if (grh.numFile) {
                        this.inits
                            .loadImage(grh.numFile)
                            .then(() => {
                                this.drawGrhCapa(capa, grh, x, y, alpha);
                            })
                            .catch(() => {
                                if (grh.grhIndex) {
                                    delete this.inits.graphics[grh.grhIndex];
                                }

                                console.log("Error :(");
                            });
                    }
                }
            }
        } catch (err) {
            dumpError(err);
        }
    };

    charRender = (personaje, pixelOffsetX, pixelOffsetY) => {
        try {
            let moved = false;

            if (personaje.moving) {
                if (personaje.scrollDirectionX) {
                    personaje.moveOffsetX =
                        personaje.moveOffsetX +
                        this.scrollPixelsPerFrameX *
                            personaje.scrollDirectionX *
                            this.timerTicksPerFrame;

                    moved = true;

                    if (
                        (this.sign(personaje.scrollDirectionX) == 1 &&
                            personaje.moveOffsetX >= 0) ||
                        (this.sign(personaje.scrollDirectionX) == -1 &&
                            personaje.moveOffsetX <= 0)
                    ) {
                        personaje.moveOffsetX = 0;
                        personaje.scrollDirectionX = 0;
                    }
                }

                if (personaje.scrollDirectionY) {
                    personaje.moveOffsetY =
                        personaje.moveOffsetY +
                        this.scrollPixelsPerFrameY *
                            personaje.scrollDirectionY *
                            this.timerTicksPerFrame;

                    moved = true;

                    if (
                        (this.sign(personaje.scrollDirectionY) == 1 &&
                            personaje.moveOffsetY >= 0) ||
                        (this.sign(personaje.scrollDirectionY) == -1 &&
                            personaje.moveOffsetY <= 0)
                    ) {
                        personaje.moveOffsetY = 0;
                        personaje.scrollDirectionY = 0;
                    }
                }
            }

            if (!moved) {
                personaje.frameCounter = 1;
                personaje.frameCounterWeapon = 1;
                personaje.frameCounterShield = 1;
                personaje.moving = false;
            }

            this.drawChar(
                personaje,
                pixelOffsetX + personaje.moveOffsetX,
                pixelOffsetY + personaje.moveOffsetY
            );

            if (personaje.fxId) {
                this.drawFx(
                    personaje,
                    pixelOffsetX + personaje.moveOffsetX,
                    pixelOffsetY + personaje.moveOffsetY,
                    true
                );
            }
        } catch (err) {
            dumpError(err);
        }
    };

    // v2026-06-25 revert: el filtro de sombraOscura se quitó —
    // ahora TODAS las palabras (chat, spells, etc.) de un char en
    // sombra son visibles para otros viewers. El sigilo sigue siendo
    // del CUERPO (alpha 0) pero las palabras se ven igual.
    // El server clampa la duración a 0.5s para chars en sombra,
    // así que las palabras no se quedan visibles indefinidamente.
    _shouldRenderDialog = (personaje) => {
        return !!this.config.dialogs[personaje.id];
    };

    drawChar = (personaje, sX, sY) => {
        try {
            // Sombra Oscura de Morgolock (#49):
            //   - Self → alpha 0.35 (visible para uno mismo, sabes que sigues
            //     ahí pero notablemente fundido en sombra).
            //   - Otro → SKIP draw entirely (invisible total — sin blip,
            //     sin fade, sin trace).
            // v2026-06-25: El Ojo del Demiurgo (#43) bypassea
            // sombraOscura + invisible — NO sigiloAtidorfa (el spell
            // de desvanecimiento más poderoso, inmune a la visión divina).
            //
            // Perf: estos bools se computan por char por frame. `iHaveOjo`
            // y `isNpc` son property lookups baratos, no hace falta cachear
            // por encima del per-call.
            const isSelf_ = personaje.id === this.user.id;
            const iHaveOjo =
                this.user.ojoDemiurgo && !personaje.isNpc;
            // "Body invisible" agrupa sombraOscura + sigiloAtidorfa para
            // el render (ambos esconden el cuerpo igual).
            const isBodyInvisible =
                personaje.sombraOscura || personaje.sigiloAtidorfa;
            if (
                iHaveOjo &&
                !isSelf_ &&
                !personaje.sigiloAtidorfa &&
                (personaje.sombraOscura || personaje.invisible)
            ) {
                // Render el char con alpha 0.38 — visible para mí pero
                // claramente "fantasmal". Skip toda la lógica de
                // sombra/invisible que viene abajo.
                this.canvas.foreground.ctx.save();
                this.canvas.foreground.ctx.globalAlpha = 0.38;
                this._invisibleBlipActive = true;   // reuso el flag de restore
                // Cae al render normal (sin entrar en sombra/invisible branch).
            } else if (isBodyInvisible) {
                const isSelfSO = personaje.id === this.user.id;
                if (!isSelfSO) {
                    // v2026-06-25: render dialog antes del return — gate
                    // `_shouldRenderDialog` chequea sombraOscura + bypassStealth,
                    // así que solo las palabras de Sombra Oscura (las únicas
                    // con bypassStealth=1) se renderean.
                    if (this._shouldRenderDialog(personaje)) {
                        const dMsg = this.config.dialogs[personaje.id].msg;
                        const dLen = dMsg.length;
                        const firstLineLen = dLen >= 30 ? 45 : dLen * 3;
                        const dialogX = sX + 32 - firstLineLen;
                        this.renderDialog(
                            personaje.id,
                            dialogX,
                            sY +
                                this.inits.bodies[personaje.idBody]
                                    .headOffsetY -
                                this.config.dialogs[personaje.id].y,
                            this.config.dialogs[personaje.id].color,
                            true,
                            13,
                            "Tahoma"
                        );
                    }
                    return; // others nunca ven al char
                }
                // self: render con alpha bajo
                this.canvas.foreground.ctx.save();
                this.canvas.foreground.ctx.globalAlpha = 0.35;
                this._sombraOscuraSelfActive = true;
            } else if (personaje.invisible) {
                const now = Date.now();
                const isSelf = personaje.id === this.user.id;
                // Fade activo? (700ms desde el setInvisible).
                let fadeT = 1; // 1 = fade completo, alpha en target final.
                if (personaje.invisibilityFadeEnd && personaje.invisibilityFadeEnd > now) {
                    const fadeDur = personaje.invisibilityFadeEnd - personaje.invisibilityFadeStart;
                    const fadeElapsed = now - personaje.invisibilityFadeStart;
                    fadeT = Math.max(0, Math.min(1, fadeElapsed / fadeDur));
                }
                if (isSelf) {
                    // Self: alpha interpolada de 1.0 → 0.4 durante el fade,
                    // luego estático 0.4.
                    const alpha = 1.0 - (1.0 - 0.4) * fadeT;
                    this.canvas.foreground.ctx.save();
                    this.canvas.foreground.ctx.globalAlpha = alpha;
                    this._invisibleSelfActive = true;
                } else {
                    // Other: durante fade va de 1.0 → 0 (gradual hide).
                    // Después del fade, override con blip si activo.
                    let alpha = 0;
                    if (fadeT < 1) {
                        alpha = 1.0 - fadeT;
                    } else if (personaje.blipEnd && personaje.blipEnd > now) {
                        const blipDur = personaje.blipEnd - personaje.blipStart;
                        const blipElapsed = now - personaje.blipStart;
                        const t = Math.max(0, Math.min(1, blipElapsed / blipDur));
                        // v2026-06-25 final: split 650ms in / 450ms hold /
                        // 650ms out sobre un blip de 1750ms total.
                        // En ratios: 0.3714 fade-in, 0.2571 hold, 0.3714
                        // fade-out. Hold start = 650/1750, hold end =
                        // 1100/1750. Si server cambia BLIP_DURATION_MS,
                        // las proporciones se mantienen (no los ms).
                        const fadeRatio = 650 / 1750; // ≈ 0.3714
                        if (t < fadeRatio) alpha = (t / fadeRatio) * 0.4;
                        else if (t < 1 - fadeRatio) alpha = 0.4;
                        else alpha = ((1 - t) / fadeRatio) * 0.4;
                    }
                    if (alpha <= 0.01) {
                        // v2026-06-25: render dialog ANTES del early return.
                        // El dialog usa `textos.ctx` (canvas separado, NO
                        // afectado por foreground.ctx alpha), pero como el
                        // bloque que lo renderea estaba más abajo en drawChar,
                        // el early return lo dejaba sin renderear cuando el
                        // char era totalmente invisible. Ahora siempre se
                        // renderea sus 5s naturales de lifetime, incluso si
                        // el char está oculto (palabras mágicas del cast deben
                        // ser visibles aunque el caster se haya esfumado).
                        if (this._shouldRenderDialog(personaje)) {
                            const dMsg = this.config.dialogs[personaje.id].msg;
                            const dLen = dMsg.length;
                            const firstLineLen = dLen >= 30 ? 45 : dLen * 3;
                            const dialogX = sX + 32 - firstLineLen;
                            this.renderDialog(
                                personaje.id,
                                dialogX,
                                sY +
                                    this.inits.bodies[personaje.idBody]
                                        .headOffsetY -
                                    this.config.dialogs[personaje.id].y,
                                this.config.dialogs[personaje.id].color,
                                true,
                                13,
                                "Tahoma"
                            );
                        }
                        return;
                    }
                    this.canvas.foreground.ctx.save();
                    this.canvas.foreground.ctx.globalAlpha = alpha;
                    this._invisibleBlipActive = true;
                }
            }
            // Ticket #298: tint del sprite (white para Dragón Blanco, etc.).
            // Aplicado DESPUÉS del save de invisibility para que ambos coexistan
            // (un dragon invisible se ve tinted + alpha). Restaurado al final
            // del drawChar.
            const charTint = personaje.tint;
            const useCharTint = charTint === "white" || charTint === "black" ||
                                charTint === "blue" || charTint === "green" ||
                                charTint === "red";
            if (useCharTint) {
                this.canvas.foreground.ctx.save();
                this.canvas.foreground.ctx.filter = this._filterForTint(charTint);
                this._charTintActive = true;
            }
            const nameLength = personaje.nameCharacter.length * 3.5;

            if (this.delta) {
                let grhRopa = "";
                let grhWeapon = "";
                let grhShield = "";

                if (personaje.idBody) {
                    grhRopa = this.inits.bodies[personaje.idBody][
                        personaje.heading
                    ];
                }

                if (personaje.idWeapon) {
                    grhWeapon = this.inits.armas[personaje.idWeapon][
                        personaje.heading
                    ];
                }

                if (personaje.idShield) {
                    grhShield = this.inits.escudos[personaje.idShield][
                        personaje.heading
                    ];
                }

                if (personaje.moving) {
                    personaje.frameCounter +=
                        this.delta / this.inits.graphics[grhRopa].speed;

                    if (
                        Math.ceil(personaje.frameCounter) >
                        this.inits.graphics[grhRopa].numFrames
                    ) {
                        personaje.frameCounter = 0;
                        personaje.frameCounter +=
                            this.delta / this.inits.graphics[grhRopa].speed;
                    }

                    if (grhWeapon) {
                        personaje.frameCounterWeapon +=
                            this.delta / this.inits.graphics[grhWeapon].speed;

                        if (
                            Math.ceil(personaje.frameCounterWeapon) >
                            this.inits.graphics[grhWeapon].numFrames
                        ) {
                            personaje.frameCounterWeapon = 0;
                            personaje.frameCounterWeapon +=
                                this.delta /
                                this.inits.graphics[grhWeapon].speed;
                        }
                    }

                    if (grhShield) {
                        personaje.frameCounterShield +=
                            this.delta / this.inits.graphics[grhShield].speed;

                        if (
                            Math.ceil(personaje.frameCounterShield) >
                            this.inits.graphics[grhShield].numFrames
                        ) {
                            personaje.frameCounterShield = 0;
                            personaje.frameCounterShield +=
                                this.delta /
                                this.inits.graphics[grhShield].speed;
                        }
                    }
                }

                if (
                    personaje.frameCounter > 0 &&
                    Math.ceil(personaje.frameCounter) <=
                        this.inits.graphics[grhRopa].numFrames
                ) {
                    let graphicsGrhRopa = "";
                    let graphicsGrhWeapon = "";
                    let graphicsGrhShield = "";

                    if (grhRopa) {
                        const CurrentGrhRopa = this.inits.graphics[grhRopa]
                            .frames[Math.ceil(personaje.frameCounter)];
                        graphicsGrhRopa = this.inits.graphics[CurrentGrhRopa];
                    }

                    if (grhWeapon) {
                        const CurrentGrhWeapon = this.inits.graphics[grhWeapon]
                            .frames[Math.ceil(personaje.frameCounterWeapon)];
                        graphicsGrhWeapon = this.inits.graphics[
                            CurrentGrhWeapon
                        ];
                    }

                    if (grhShield) {
                        const CurrentGrhShield = this.inits.graphics[grhShield]
                            .frames[Math.ceil(personaje.frameCounterShield)];
                        graphicsGrhShield = this.inits.graphics[
                            CurrentGrhShield
                        ];
                    }

                    if (personaje.idHead) {
                        const grhCabeza = this.inits.heads[personaje.idHead][
                            personaje.heading
                        ];

                        const graphicGrhHead = this.inits.graphics[grhCabeza];

                        const widtHead = graphicGrhHead.width;
                        const heightHead = graphicGrhHead.height;

                        if (
                            this.inits.preCacheGraphics[graphicGrhHead.numFile]
                        ) {
                            this.canvas.foreground.ctx.drawImage(
                                this.inits.preCacheGraphics[
                                    graphicGrhHead.numFile
                                ],
                                graphicGrhHead.sX,
                                graphicGrhHead.sY,
                                widtHead,
                                heightHead,
                                sX + this.config.OffsetXHead,
                                sY +
                                    this.inits.bodies[personaje.idBody]
                                        .headOffsetY -
                                    18,
                                widtHead,
                                heightHead
                            );
                        } else {
                            this.inits.loadImage(graphicGrhHead.numFile);
                        }
                    }

                    if (graphicsGrhRopa) {
                        const widthRopa = graphicsGrhRopa.width;
                        const heightRopa = graphicsGrhRopa.height;

                        const tmpsX =
                            sX - Math.floor((widthRopa * 16) / 32) + 16;
                        const tmpsY =
                            sY - Math.floor((heightRopa * 32) / 32) + 32;

                        if (
                            this.inits.preCacheGraphics[graphicsGrhRopa.numFile]
                        ) {
                            this.canvas.foreground.ctx.drawImage(
                                this.inits.preCacheGraphics[
                                    graphicsGrhRopa.numFile
                                ],
                                graphicsGrhRopa.sX,
                                graphicsGrhRopa.sY,
                                widthRopa,
                                heightRopa,
                                tmpsX,
                                tmpsY,
                                widthRopa,
                                heightRopa
                            );
                        } else {
                            this.inits.loadImage(graphicsGrhRopa.numFile);
                        }
                    }

                    if (personaje.idHelmet) {
                        const casco = this.inits.cascos[personaje.idHelmet];
                        const grhHelmet = this.inits.cascos[personaje.idHelmet][
                            personaje.heading
                        ];

                        const graphicGrhHelmet = this.inits.graphics[grhHelmet];

                        const widtHelmet = graphicGrhHelmet.width;
                        const heightHelmet = graphicGrhHelmet.height;

                        if (
                            this.inits.preCacheGraphics[
                                graphicGrhHelmet.numFile
                            ]
                        ) {
                            this.canvas.foreground.ctx.drawImage(
                                this.inits.preCacheGraphics[
                                    graphicGrhHelmet.numFile
                                ],
                                graphicGrhHelmet.sX,
                                graphicGrhHelmet.sY,
                                widtHelmet,
                                heightHelmet,
                                sX + this.config.OffsetXHead + casco.offsetX,
                                sY +
                                    this.inits.bodies[personaje.idBody]
                                        .headOffsetY -
                                    18 +
                                    casco.offsetY,
                                widtHelmet,
                                heightHelmet
                            );
                        } else {
                            this.inits.loadImage(graphicGrhHelmet.numFile);
                        }
                    }

                    if (graphicsGrhWeapon) {
                        const widthWeapon = graphicsGrhWeapon.width;
                        const heightWeapon = graphicsGrhWeapon.height;

                        const tmpsX =
                            sX - Math.floor((widthWeapon * 16) / 32) + 16;
                        const tmpsY =
                            sY - Math.floor((heightWeapon * 32) / 32) + 28;

                        if (
                            this.inits.preCacheGraphics[
                                graphicsGrhWeapon.numFile
                            ]
                        ) {
                            this.canvas.foreground.ctx.drawImage(
                                this.inits.preCacheGraphics[
                                    graphicsGrhWeapon.numFile
                                ],
                                graphicsGrhWeapon.sX,
                                graphicsGrhWeapon.sY,
                                widthWeapon,
                                heightWeapon,
                                tmpsX,
                                tmpsY,
                                widthWeapon,
                                heightWeapon
                            );
                        } else {
                            this.inits.loadImage(graphicsGrhWeapon.numFile);
                        }
                    }

                    if (graphicsGrhShield) {
                        const widthShield = graphicsGrhShield.width;
                        const heightShield = graphicsGrhShield.height;

                        const tmpsX =
                            sX - Math.floor((widthShield * 16) / 32) + 16;
                        const tmpsY =
                            sY - Math.floor((heightShield * 32) / 32) + 32;

                        if (
                            this.inits.preCacheGraphics[
                                graphicsGrhShield.numFile
                            ]
                        ) {
                            this.canvas.foreground.ctx.drawImage(
                                this.inits.preCacheGraphics[
                                    graphicsGrhShield.numFile
                                ],
                                graphicsGrhShield.sX,
                                graphicsGrhShield.sY,
                                widthShield,
                                heightShield,
                                tmpsX,
                                tmpsY,
                                widthShield,
                                heightShield
                            );
                        } else {
                            this.inits.loadImage(graphicsGrhShield.numFile);
                        }
                    }

                    const nameX = sX - nameLength + 16;

                    this.renderText2(
                        personaje.nameCharacter,
                        nameX,
                        sY + 45,
                        personaje.color,
                        true,
                        13,
                        "Tahoma"
                    );

                    // Health bar del NPC — solo visible al atacante. El
                    // server manda updateNpcHp tras cada damage, el
                    // cliente setea hp/maxHp/healthBarUntil acá. Si la
                    // bar expiró (8s sin damage), no se dibuja. Usamos
                    // this._frameNow (cacheado al inicio de renderScreen)
                    // en vez de Date.now() para evitar syscalls/frame.
                    if (
                        personaje.isNpc &&
                        personaje.maxHp > 0 &&
                        personaje.healthBarUntil &&
                        this._frameNow < personaje.healthBarUntil
                    ) {
                        this.renderNpcHealthBar(
                            sX + 16,
                            sY + 50,
                            personaje.hp || 0,
                            personaje.maxHp
                        );
                    }

                    // Status bar (paralizar/inmovilizar) — dorada Atrium,
                    // justo debajo de la HP. Visible mientras el efecto
                    // dura. Auto-oculta cuando expira (start + duration).
                    if (
                        personaje.isNpc &&
                        personaje.statusType &&
                        personaje.statusDuration &&
                        this._frameNow <
                            personaje.statusStart + personaje.statusDuration
                    ) {
                        this.renderNpcStatusBar(
                            sX + 16,
                            sY + 56,
                            personaje.statusStart,
                            personaje.statusDuration,
                            this._frameNow
                        );
                    }

                    if (personaje.clan) {
                        var clanLength = personaje.clan.length * 4;
                        const clanX = sX - clanLength + 16;

                        this.renderText2(
                            personaje.clan,
                            clanX,
                            sY + 65,
                            personaje.color,
                            true,
                            13,
                            "Tahoma"
                        );
                    }

                    if (this._shouldRenderDialog(personaje)) {
                        const textLength = this.config.dialogs[personaje.id].msg
                            .length;
                        let firstLineLength;

                        if (textLength >= 30) {
                            firstLineLength = 45;
                        } else {
                            firstLineLength = textLength * 3;
                        }

                        const dialogX = sX + 32 - firstLineLength;

                        this.renderDialog(
                            personaje.id,
                            dialogX,
                            sY +
                                this.inits.bodies[personaje.idBody]
                                    .headOffsetY -
                                this.config.dialogs[personaje.id].y,
                            this.config.dialogs[personaje.id].color,
                            true,
                            13,
                            "Tahoma"
                        );
                    }
                }
            }
        } catch (err) {
            dumpError(err);
        } finally {
            // Ticket #298: restore tint primero (LIFO — save de tint fue el
            // último). Si invisibility no estaba activa, solo este restore.
            if (this._charTintActive) {
                this.canvas.foreground.ctx.restore();
                this._charTintActive = false;
            }
            // Restaurar globalAlpha si lo habíamos bajado por invisibilidad.
            // (#289) Ahora cubre los 2 casos: self-invisible y blip de
            // otro char invisible. Ambos hicieron ctx.save() arriba.
            if (
                this._invisibleSelfActive ||
                this._invisibleBlipActive ||
                this._sombraOscuraSelfActive
            ) {
                this.canvas.foreground.ctx.restore();
                this._invisibleSelfActive = false;
                this._invisibleBlipActive = false;
                this._sombraOscuraSelfActive = false;
            }
        }
    };

    drawFx = (personaje, sX, sY, alpha) => {
        try {
            if (this.delta) {
                const grh = this.inits.fxs[personaje.fxId].grh;

                personaje.frameFxCounter +=
                    this.delta / this.inits.graphics[grh].speed;

                if (
                    Math.ceil(personaje.frameFxCounter) >
                    this.inits.graphics[grh].numFrames
                ) {
                    personaje.frameFxCounter = 0;
                    personaje.frameFxCounter +=
                        this.delta / this.inits.graphics[grh].speed;
                    if (
                        personaje.fxId != 4 &&
                        personaje.fxId != 5 &&
                        personaje.fxId != 6 &&
                        personaje.fxId != 16 &&
                        personaje.fxId != 34
                    ) {
                        personaje.fxId = 0;
                    }
                }

                if (
                    personaje.frameFxCounter > 0 &&
                    Math.ceil(personaje.frameFxCounter) <=
                        this.inits.graphics[grh].numFrames &&
                    personaje.fxId
                ) {
                    const CurrentGrhIndex = this.inits.graphics[grh].frames[
                        Math.ceil(personaje.frameFxCounter)
                    ];

                    const graphicCurrentGrh = this.inits.graphics[
                        CurrentGrhIndex
                    ];

                    const width = graphicCurrentGrh.width;
                    const height = graphicCurrentGrh.height;

                    sX += this.inits.fxs[personaje.fxId].offsetX;
                    sY += this.inits.fxs[personaje.fxId].offsetY;

                    if (alpha) {
                        this.canvas.foreground.ctx.globalAlpha = 0.6;
                    }

                    if (
                        this.inits.preCacheGraphics[graphicCurrentGrh.numFile]
                    ) {
                        // Tinted FX: si el catalog fxs marca `tint: 'red'`
                        // (ej. Furia de Uhkrul fxId 36), pre-componemos el
                        // sprite en un offscreen canvas y aplicamos color
                        // multiply, así el tint queda restringido a los
                        // píxeles no-transparentes del FX (no se "pinta"
                        // el char debajo).
                        const fxCatalog =
                            this.inits.fxs[personaje.fxId] || {};
                        const tint = fxCatalog.tint;
                        if (tint) {
                            const off = this._getTintOffscreen(width, height);
                            const octx = off.ctx;
                            octx.clearRect(0, 0, width, height);
                            octx.globalCompositeOperation = "source-over";
                            octx.drawImage(
                                this.inits.preCacheGraphics[
                                    graphicCurrentGrh.numFile
                                ],
                                graphicCurrentGrh.sX,
                                graphicCurrentGrh.sY,
                                width,
                                height,
                                0,
                                0,
                                width,
                                height
                            );
                            octx.globalCompositeOperation = "source-atop";
                            octx.fillStyle =
                                tint === "red"
                                    ? "rgba(255, 30, 30, 0.7)"
                                    : tint;
                            octx.fillRect(0, 0, width, height);
                            this.canvas.foreground.ctx.drawImage(
                                off.canvas,
                                0, 0, width, height,
                                sX, sY, width, height
                            );
                        } else {
                            this.canvas.foreground.ctx.drawImage(
                                this.inits.preCacheGraphics[
                                    graphicCurrentGrh.numFile
                                ],
                                graphicCurrentGrh.sX,
                                graphicCurrentGrh.sY,
                                width,
                                height,
                                sX,
                                sY,
                                width,
                                height
                            );
                        }
                    } else {
                        this.inits.loadImage(graphicCurrentGrh.numFile);
                    }

                    if (alpha) {
                        this.canvas.foreground.ctx.globalAlpha = 1;
                    }
                }
            }
        } catch (err) {
            dumpError(err);
        }
    };

    renderDialog = (id, x, y, color, bold, fontSize, fontType) => {
        try {
            // Font string en una sola alloc (template literal) en vez de
            // 3 strings intermedios por concat — mismo cambio que en
            // renderText/renderText2.
            this.canvas.textos.ctx.font = `${bold ? "bold" : ""}${
                fontSize ? " " + fontSize + "px" : ""
            }${fontType ? " " + fontType : ""}`;

            if (color) {
                this.canvas.textos.ctx.fillStyle = color;
            }

            const text = this.config.dialogs[id].msg;
            const words = text.split(" ");
            let wordsLine = "";
            let count = 0;
            let currentLine = 0;
            const lineHeight = 20;
            const cantWordsPerLine = 4;
            let posYText = 0;

            const wordsLength = words.length;
            const textLength = text.length;

            if (wordsLength > 4 && textLength >= 30) {
                posYText =
                    (Math.ceil(words.length / cantWordsPerLine) - 1) * 20;
            }

            for (let i = 0; i < wordsLength; i++) {
                if (count <= cantWordsPerLine || textLength < 30) {
                    wordsLine += words[i] + " ";
                }

                count++;

                if (
                    (count >= cantWordsPerLine && textLength >= 30) ||
                    i == wordsLength - 1
                ) {
                    const textPosY = y + lineHeight * currentLine++ - posYText;

                    this.canvas.textos.ctx.strokeStyle = "#000000";
                    this.canvas.textos.ctx.strokeText(
                        wordsLine,
                        x - 20,
                        textPosY
                    );
                    this.canvas.textos.ctx.fillText(
                        wordsLine,
                        x - 20,
                        textPosY
                    );
                    wordsLine = "";
                    count = 0;
                }
            }

            // v2026-06-25: expiración wall-clock (Date.now() >= expiresAt)
            // en vez de contar frames. Garantiza que caster y observers
            // vean la palabra exactamente el mismo tiempo, sin importar
            // diferencias de fps entre browsers. El `life` counter sigue
            // existiendo solo para el float upward del texto (primeros
            // 10 frames). Fallback: si por algún motivo no llegó
            // `expiresAt` (dialog legacy o creado sin packet), usar
            // life > 120 (~2s @ 60fps) como cap defensivo.
            const expiresAt = this.config.dialogs[id].expiresAt;
            const expired = expiresAt
                ? Date.now() >= expiresAt
                : this.config.dialogs[id].life > 120;
            if (expired) {
                delete this.config.dialogs[id];
            } else {
                if (this.config.dialogs[id].y < 30) {
                    this.config.dialogs[id].y++;
                }
                this.config.dialogs[id].life++;
            }
        } catch (err) {
            dumpError(err);
        }
    };

    // Construye (o busca en cache) un canvas off-screen con el text label
    // rendereado. Match exacto del visual de fillText/strokeText originales:
    // outline negro + fill colored, alphabetic baseline. Devuelve
    // Offscreen canvas reusable para tintar sprites de FX. Una sola instancia
    // que se redimensiona si el FX es más grande que el actual. Retorna
    // { canvas, ctx } — el caller dibuja el sprite en (0,0) y aplica
    // composite operations sin afectar el canvas principal.
    _getTintOffscreen = (width, height) => {
        if (!this._tintOffscreen) {
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(64, width);
            canvas.height = Math.max(64, height);
            this._tintOffscreen = {
                canvas,
                ctx: canvas.getContext("2d")
            };
        } else if (
            this._tintOffscreen.canvas.width < width ||
            this._tintOffscreen.canvas.height < height
        ) {
            this._tintOffscreen.canvas.width = Math.max(
                this._tintOffscreen.canvas.width, width
            );
            this._tintOffscreen.canvas.height = Math.max(
                this._tintOffscreen.canvas.height, height
            );
        }
        return this._tintOffscreen;
    };

    // { canvas, baseY, padX } — baseY = donde cae la baseline del texto
    // dentro del canvas (offset desde top), padX = padding izquierdo.
    //
    // El caller blitea con `drawImage(canvas, x - padX, y - baseY)` para
    // matchear visualmente el `fillText(text, x, y)` original.
    _getTextCanvas = (text, color, bold, fontSize, fontType) => {
        const fs = fontSize || 12;
        const key =
            text + "|" + (color || "") + "|" + (bold ? "b" : "") + "|" + fs + "|" + (fontType || "");
        let entry = this._textCanvasCache.get(key);
        if (entry) {
            // LRU bump: re-insertar para que quede al final del Map.
            this._textCanvasCache.delete(key);
            this._textCanvasCache.set(key, entry);
            return entry;
        }
        // Build new entry.
        const font =
            (bold ? "bold " : "") + fs + "px" + (fontType ? " " + fontType : "");
        const off = document.createElement("canvas");
        const offCtx = off.getContext("2d");
        offCtx.font = font;
        const w = Math.max(1, Math.ceil(offCtx.measureText(text).width));
        const padX = 2;       // espacio para el stroke a la izquierda
        const padY = 3;       // espacio para descender + stroke abajo
        off.width = w + padX * 2;
        off.height = fs + padY * 2;
        // Resize del canvas resetea el ctx state → reset font.
        offCtx.font = font;
        offCtx.fillStyle = color || "#FFFFFF";
        offCtx.strokeStyle = "#000000";
        offCtx.textBaseline = "alphabetic";
        // Baseline a fs (justo debajo del padding-top), con padX horizontal.
        const baseY = fs;
        offCtx.strokeText(text, padX, baseY);
        offCtx.fillText(text, padX, baseY);
        entry = { canvas: off, baseY: baseY, padX: padX };
        // LRU cap.
        if (this._textCanvasCache.size >= this._textCacheCap) {
            const firstKey = this._textCanvasCache.keys().next().value;
            this._textCanvasCache.delete(firstKey);
        }
        this._textCanvasCache.set(key, entry);
        return entry;
    };

    renderText = (text, x, y, color, bold, fontSize, fontType) => {
        try {
            const ctx = this.canvas.textos.ctx;
            const entry = this._getTextCanvas(
                text,
                color,
                bold,
                fontSize,
                fontType
            );
            // drawImage con offset que matchea el comportamiento de
            // ctx.fillText(text, x, y) original (baseline alphabetic en y).
            ctx.drawImage(entry.canvas, x - entry.padX, y - entry.baseY);
        } catch (err) {
            dumpError(err);
        }
    };

    // Barra de vida minimalista del NPC — Atrium Frame style. Render
    // sobre el canvas .foreground del engine. Posición (centerX, topY):
    // centerX = sX + 16 (centro horizontal del sprite, ya que sX es el
    // borde izquierdo y el tile es de 32px wide → centro a +16). topY
    // viene del caller, típicamente sY + 50 (debajo del name a sY+45).
    //
    // Estilo: 40px ancho × 4px alto, gradient dorado tenue → vivo.
    // Border dorado Atrium 0.55 alpha, track casi negro semi-translúcido
    // con subtle inner shadow. Sin glow externo para mantener super
    // minimalista (no compite visualmente con sprites del juego).
    renderNpcHealthBar = (centerX, topY, hp, maxHp) => {
        try {
            const ctx = this.canvas.foreground.ctx;
            if (!ctx) return;
            const W = 40;
            const H = 4;
            const x = Math.round(centerX - W / 2);
            const y = Math.round(topY);
            const ratio = Math.max(0, Math.min(1, hp / maxHp));

            // Track — fondo casi negro semi-translúcido (paleta Atrium).
            ctx.fillStyle = "rgba(11, 14, 18, 0.92)";
            ctx.fillRect(x, y, W, H);

            // Fill — color sólido rojo (a 4px de alto el gradient era
            // imperceptible y construirlo cuesta GC). En low HP (≤25%)
            // vira a un rojo más saturado y brillante como alerta visual.
            const fillW = Math.round((W - 2) * ratio);
            if (fillW > 0) {
                ctx.fillStyle =
                    ratio <= 0.25
                        ? "rgba(232, 60, 60, 1)"
                        : "rgba(200, 50, 50, 0.92)";
                ctx.fillRect(x + 1, y + 1, fillW, H - 2);
            }

            // Border — dorado Atrium alpha 0.55 (mantiene el lenguaje
            // visual del HUD, no rompe con todo el resto). 1px crisp
            // con offset .5 para evitar antialiasing borroso en canvas.
            ctx.strokeStyle = "rgba(212, 130, 50, 0.55)";
            ctx.lineWidth = 1;
            ctx.strokeRect(x + 0.5, y + 0.5, W - 1, H - 1);
        } catch (err) {
            // Render errors no deben romper el game loop.
        }
    };

    // Status bar (paralizar/inmovilizar) — dorada Atrium minimalista,
    // misma dimensión que la HP bar pero el fill cuenta hacia atrás
    // mostrando cuánto del cooldown queda. Drena de izquierda a derecha
    // hasta vaciarse cuando termina el efecto. El caller pasa `now` ya
    // cacheado (Date.now() de renderScreen) para evitar la syscall.
    renderNpcStatusBar = (centerX, topY, startedAt, durationMs, now) => {
        try {
            const ctx = this.canvas.foreground.ctx;
            if (!ctx) return;
            const W = 40;
            const H = 4;
            const x = Math.round(centerX - W / 2);
            const y = Math.round(topY);
            const elapsed = (now || Date.now()) - startedAt;
            const ratio = Math.max(0, 1 - elapsed / durationMs);
            if (ratio <= 0) return;

            // Track — fondo Atrium gradient bg color (mismo que HP bar).
            ctx.fillStyle = "rgba(11, 14, 18, 0.92)";
            ctx.fillRect(x, y, W, H);

            // Fill — color sólido dorado Atrium (gradient imperceptible
            // a 4px, gastaba GC para nada). Drena con el tiempo restante.
            const fillW = Math.round((W - 2) * ratio);
            if (fillW > 0) {
                ctx.fillStyle = "rgba(228, 145, 60, 0.92)";
                ctx.fillRect(x + 1, y + 1, fillW, H - 2);
            }

            // Border — dorado Atrium 0.55 alpha (igual que HP bar).
            ctx.strokeStyle = "rgba(212, 130, 50, 0.55)";
            ctx.lineWidth = 1;
            ctx.strokeRect(x + 0.5, y + 0.5, W - 1, H - 1);
        } catch (err) {
            // Render errors no deben romper el game loop.
        }
    };

    renderText2 = (text, x, y, color, bold, fontSize, fontType) => {
        try {
            // Hot path: NPC + char names + clans, llamado por cada entity
            // en viewport cada frame (~3,600 calls/seg en zona poblada).
            // Cache off-screen + drawImage en vez de fillText/strokeText
            // directos. Ver comentario en constructor (_textCanvasCache).
            const ctx = this.canvas.foreground.ctx;
            const entry = this._getTextCanvas(
                text,
                color,
                bold,
                fontSize,
                fontType
            );
            ctx.drawImage(entry.canvas, x - entry.padX, y - entry.baseY);
        } catch (err) {
            dumpError(err);
        }
    };

    moveTo = (userId, direccion) => {
        if (+Date.now() - this.timeWalk > this.config.timeWalkMS) {
            if (!this.user.inmovilizado) {
                if (this.personajes[userId]) {
                    this.moveScreen(direccion, false);
                }
            } else {
                this.pkg.setPackageID(this.pkg.serverPacketID.position);
                this.pkg.writeByte(direccion);
                this.config.ws.send(this.pkg.dataSend());

                this.personajes[userId].heading = direccion;

                this.timeWalk = +Date.now();
            }
        }
    };

    moveScreen = (direccion, dontSend) => {
        try {
            let x = 0;
            let y = 0;
            let tX = 0;
            let tY = 0;

            switch (direccion) {
                case this.config.direcciones.up:
                    y = -1;
                    break;

                case this.config.direcciones.right:
                    x = 1;
                    break;

                case this.config.direcciones.down:
                    y = 1;
                    break;

                case this.config.direcciones.left:
                    x = -1;
                    break;
            }

            tX = this.user.pos.x + x;
            tY = this.user.pos.y + y;

            if (this.legalPos(tX, tY, this.user.navegando)) {
                this.user.addtoUserPos.x = x;
                this.user.pos.x = tX;
                this.user.addtoUserPos.y = y;
                this.user.pos.y = tY;
                this.user.moving = 1;

                //changePositionPointMinimap(tX, tY);

                this.moveCharByHead(this.user.id, direccion, dontSend);

                // SFX de pasos al caminar en tierra. No suenan si:
                //   - estás navegando (raro escuchar pasos arriba de un barco)
                //   - estás muerto/fantasma (los espíritus no hacen ruido)
                // El estado "muerto" vive en personajes[id].muerto (lo
                // setea putBodyAndHeadDead del server), NO en this.user
                // — por eso hay que mirar la entrada del propio char en
                // el dict personajes.
                const me = this.personajes[this.user.id];
                const isDead = me && me.muerto;
                // Sombra Oscura ya NO mutea los pasos. Sigilo de Atidorfa
                // sí los mutea (versión mejorada — silencio total).
                if (!this.user.navegando && !isDead && !this.user.sigiloAtidorfa) {
                    const audioManager = require("./audio").default;
                    audioManager.playSteps();
                }
            } else {
                if (this.personajes[this.user.id].heading != direccion) {
                    this.personajes[this.user.id].heading = direccion;

                    this.pkg.setPackageID(
                        this.pkg.serverPacketID.changeHeading
                    );
                    this.pkg.writeByte(direccion);
                    this.config.ws.send(this.pkg.dataSend());
                }
                // Nota: el char ya da feedback visual girando hacia la
                // dirección bloqueada. Mensaje de chat "No se puede
                // caminar ahí" fue removido — era molesto en gameplay
                // normal donde chocar paredes es frecuente. El usuario
                // entiende visualmente que no avanzó.
            }

            this.timeWalk = +Date.now();

            // Ticket #312: incluir un primitivo distinto (_posTick) en el
            // setState. La ref `this.user` no cambia (es la misma del
            // state), entonces React podía bail-out el render del HUD —
            // el minimapa quedaba mostrando el punto en pos vieja porque
            // userPosX={user.pos.x} se calcula al render, y render no
            // se disparaba. _posTick fuerza un cambio detectable que
            // garantiza la propagación al MapBox (shouldComponentUpdate
            // sigue chequeando userPosX, así que solo re-renderea cuando
            // el pos realmente cambió).
            this.react.setState({
                user: this.user,
                _posTick: this.timeWalk
            });
        } catch (err) {
            dumpError(err);
        }
    };

    // Vestimenta de GM (#876) equipada por el user local. Habilita el
    // bypass de la puerta del Gran Consejo en legalPos. Match server
    // game.tieneVestimentaGM. Chequea idItem + equipped en el inv.
    _tieneVestimentaGM = () => {
        const items = this.user && this.user.items;
        if (!items) return false;
        for (const k in items) {
            const it = items[k];
            if (it && it.idItem === 876 && it.equipped) return true;
        }
        return false;
    };

    legalPos = (tX, tY, aguaValida) => {
        const mapa = this.inits.mapa[this.config.mapNumber];
        const mapData = this.inits.mapData[this.config.mapNumber];
        if (!mapa || !mapData) return false;
        if (!mapa[tY] || !mapa[tY][tX] || !mapData[tY] || !mapData[tY][tX]) {
            return false;
        }

        // Vestimenta de GM: traspasa la puerta del Gran Consejo de
        // Banderbill (mapa 59, tiles 38@31 y 39@31) aunque esté cerrada.
        // Match del bypass server en protocol.js:position. Solo si no hay
        // char en el tile.
        if (
            this.config.mapNumber === 59 &&
            tY === 31 &&
            (tX === 38 || tX === 39) &&
            !mapData[tY][tX].id &&
            this._tieneVestimentaGM()
        ) {
            return true;
        }

        if (tX >= 1 && tY >= 1 && tX <= 100 && tY <= 100) {
            let ret = true;

            if (!mapa[tY][tX].b) {
                if (mapData[tY][tX].id) {
                    ret = false;
                } else {
                    ret = true;
                }
            } else {
                ret = false;
            }

            if (this.hayAgua(tX, tY)) {
                if (
                    aguaValida &&
                    !this.inits.mapData[this.config.mapNumber][tY][tX].id
                ) {
                    ret = true;
                } else {
                    ret = false;
                }
            } else {
                if (aguaValida) {
                    ret = false;
                }
            }

            return ret;
        } else {
            return false;
        }
    };

    hayAgua = (tX, tY) => {
        const mapa = this.inits.mapa[this.config.mapNumber];
        if (!mapa || !mapa[tY] || !mapa[tY][tX]) return false;
        const tile = mapa[tY][tX];
        const g1 = tile.g && tile.g[1];
        const g2 = tile.g && tile.g[2];
        return (
            (g1 >= 1505 && g1 <= 1520 && !g2) ||
            (g1 >= 5665 && g1 <= 5680 && !g2) ||
            (g1 >= 13547 && g1 <= 13562 && !g2)
        );
    };

    moveCharByHead = (idPj, heading, dontSend) => {
        try {
            const personaje = this.personajes[idPj];

            if (!personaje) {
                return;
            }

            const oldX = personaje.pos.x;
            const oldY = personaje.pos.y;

            let newX = 0;
            let newY = 0;

            if (heading == this.config.direcciones.right) {
                newX = 1;
            } else if (heading == this.config.direcciones.left) {
                newX = -1;
            } else if (heading == this.config.direcciones.down) {
                newY = 1;
            } else if (heading == this.config.direcciones.up) {
                newY = -1;
            }

            const posX = oldX + newX;
            const posY = oldY + newY;

            personaje.heading = heading;

            this.inits.mapData[this.config.mapNumber][oldY][oldX].id = 0;
            this.inits.mapData[this.config.mapNumber][posY][posX].id = idPj;

            personaje.moving = true;

            personaje.pos.x = posX;
            personaje.pos.y = posY;

            personaje.moveOffsetX = -1 * (32 * newX);
            personaje.moveOffsetY = -1 * (32 * newY);

            personaje.scrollDirectionX = this.sign(newX);
            personaje.scrollDirectionY = this.sign(newY);

            // Nota: antes acá había un `audioManager.play(24)` que disparaba
            // sonido de paso para el char que se movió por heading. Bug:
            // cuando el local user se movía, esta función se llama via
            // l. 1895 y disparaba play(24), Y DESPUÉS l. 1908 disparaba
            // playSteps() — sonido doble por paso. Quitado. El paso del
            // local user vive solo en playSteps() (alternante 23/24,
            // throttle 180ms — el sonido bueno del cliente clásico).
            // Pasos de OTROS chars se manejan en moveCharByPos con
            // distance check.

            if (!dontSend) {
                this.timeWalk = +Date.now();

                this.pkg.setPackageID(this.pkg.serverPacketID.position);
                this.pkg.writeByte(heading);
                this.config.ws.send(this.pkg.dataSend());
            }
        } catch (err) {
            dumpError(err);
        }
    };

    moveCharByPos = (idPj, posX, posY) => {
        try {
            const personaje = this.personajes[idPj];

            if (!personaje) {
                return;
            }

            const oldX = personaje.pos.x;
            const oldY = personaje.pos.y;

            let newX = 0;
            let newY = 0;

            let heading = 0;

            newX = posX - oldX;
            newY = posY - oldY;

            if (this.sign(newX) == 1) {
                heading = this.config.direcciones.right;
            } else if (this.sign(newX) == -1) {
                heading = this.config.direcciones.left;
            } else if (this.sign(newY) == 1) {
                heading = this.config.direcciones.down;
            } else if (this.sign(newY) == -1) {
                heading = this.config.direcciones.up;
            }

            personaje.heading = heading;

            /*if (!legalPos(posX, posY)) {
                return;
            }*/

            this.inits.mapData[this.config.mapNumber][oldY][oldX].id = 0;
            this.inits.mapData[this.config.mapNumber][posY][posX].id = idPj;

            personaje.moving = true;

            personaje.pos.x = posX;
            personaje.pos.y = posY;

            personaje.heading = heading;

            personaje.moveOffsetX = -1 * (32 * newX);
            personaje.moveOffsetY = -1 * (32 * newY);

            personaje.scrollDirectionX = this.sign(newX);
            personaje.scrollDirectionY = this.sign(newY);

            // SFX paso de OTRO char. Esta función la dispara el server vía
            // broadcasts de position, por lo cual aquí `idPj` es típicamente
            // un char remoto. Filtros:
            //   - Skip si soy yo (mi paso lo maneja playSteps() en l. 1908,
            //     evita doble sonido si el server me echo de vuelta).
            //   - Skip si está muerto (espíritus no hacen ruido).
            //   - Distance check ≤12 tiles — cubre prácticamente todo el
            //     viewport visible (21×21, half = ~11). Si lo ves, lo oís.
            //
            // Usamos `playSteps(idPj)` (no `play(24)`) porque:
            //   1) `play()` carga 24.ogg que Chrome no decodea (Vorbis
            //      problemático) — playSteps usa WAV.
            //   2) playSteps con idPj maneja per-char throttle dentro
            //      del propio AudioManager (lastStepAtRemote dict).
            if (
                !personaje.muerto &&
                !personaje.sigiloAtidorfa &&  // sigilo mutea pasos para otros
                idPj !== this.user.id &&
                this.user.pos
            ) {
                const dx = Math.abs(posX - this.user.pos.x);
                const dy = Math.abs(posY - this.user.pos.y);
                if (dx <= 12 && dy <= 12) {
                    try {
                        const audioManager = require("./audio").default;
                        audioManager.playSteps(idPj);
                    } catch (e) {}
                }
            }
        } catch (err) {
            dumpError(err);
        }
    };

    validSendPosition = (posX, posY) => {
        if (
            this.user.pos.x < posX + 10 &&
            this.user.pos.x > posX - 10 &&
            this.user.pos.y < posY + 11 &&
            this.user.pos.y > posY - 11
        ) {
            return true;
        } else {
            return false;
        }
    };

    respawnMe = (idPj, numMap, posX, posY) => {
        try {
            const personaje = this.personajes[idPj];

            if (!personaje) {
                return;
            }

            if (this.config.mapNumber != numMap) {
                // Ticket #222: silenciar el _enemyTickInterval durante
                // la ráfaga de paquetes post-telep (setNewAreas + N×
                // getCharacter/getNpc del nuevo mapa). El tick fuerza
                // un setState sobre el componente monolítico y, si cae
                // mid-burst, suma trabajo de reconciliación al peor
                // momento → stall visible al cambiar de mapa. 400ms
                // cubre el peor caso típico de la ráfaga.
                if (this.react) {
                    this.react._mapTransitionUntil = Date.now() + 400;
                }

                // CRIT-28: limpiar chars del mapa viejo. Antes solo se
                // reseteaba el grid de mapData pero NO this.personajes —
                // los NPCs/players del mapa anterior quedaban en memoria
                // hasta el primer setNewAreas del mapa nuevo, causando
                // un flash de "fantasmas" del lugar de donde venías.
                // Preservar solo al propio user (idPj).
                for (const id in this.personajes) {
                    if (Number(id) !== idPj) {
                        delete this.personajes[id];
                    }
                }
                // Limpiar también dialogs flotantes de chars del mapa viejo.
                if (this.config.dialogs) {
                    for (const id in this.config.dialogs) {
                        if (Number(id) !== idPj) {
                            delete this.config.dialogs[id];
                        }
                    }
                }

                this.inits.mapData[this.config.mapNumber] = [];

                for (let y = 1; y <= 100; y++) {
                    this.inits.mapData[this.config.mapNumber][y] = [];

                    for (let x = 1; x <= 100; x++) {
                        this.inits.mapData[this.config.mapNumber][y][x] = {
                            id: 0
                        };
                    }
                }

                this.config.mapNumber = numMap;

                // Notificar al engine de música — telep a mapa nuevo.
                // Idempotente si el track resultado es el mismo bioma.
                try {
                    const musicEngine = require("./music").default;
                    musicEngine.setMap(numMap);
                } catch (e) {}

                // Bugfix portales TTL: si entramos a un mapa que ya cargamos
                // antes y tenía portales user-created cacheados (379 azul /
                // 380 verde / 381 rojo) que expiraron mientras estábamos
                // afuera, el .o cacheado queda stale y se renderea el portal
                // aunque server-side ya no exista. Limpiamos los 3 tipos en
                // el mapa destino — el setNewAreas que viene a continuación
                // re-emite renderItem solo para los que SIGUEN existiendo
                // (persistentes con TTL=p o creados mientras estábamos
                // afuera). NO afecta portales canónicos (objIndex 378) que
                // viven en el .map original, no en `.o` runtime.
                const targetMap = this.inits.mapa[numMap];
                if (targetMap) {
                    for (let yy = 1; yy <= 100; yy++) {
                        const row = targetMap[yy];
                        if (!row) continue;
                        for (let xx = 1; xx <= 100; xx++) {
                            const cell = row[xx];
                            if (!cell || !cell.o) continue;
                            const oi = cell.o.oi;
                            if (oi === 379 || oi === 380 || oi === 381) {
                                delete cell.o;
                                this._removeItemPosition(numMap, yy, xx);
                            }
                        }
                    }
                }

                this.user.pos.x = posX;
                this.user.pos.y = posY;

                personaje.pos.x = posX;
                personaje.pos.y = posY;

                this.inits.mapData[this.config.mapNumber][posY][posX].id = idPj;

                this.renderBackground(0, 0);
            } else {
                this.inits.mapData[this.config.mapNumber][
                    this.personajes[idPj].pos.y
                ][this.personajes[idPj].pos.x].id = 0;

                this.user.pos.x = posX;
                this.user.pos.y = posY;

                personaje.pos.x = posX;
                personaje.pos.y = posY;

                this.inits.mapData[this.config.mapNumber][posY][posX].id = idPj;

                this.renderBackground(0, 0);
            }
        } catch (err) {
            dumpError(err);
        }
    };

    respawn = (idPj, posX, posY) => {
        try {
            const personaje = this.personajes[idPj];

            if (!personaje) {
                return;
            }

            this.inits.mapData[this.config.mapNumber][
                this.personajes[idPj].pos.y
            ][this.personajes[idPj].pos.x].id = 0;

            personaje.pos.x = posX;
            personaje.pos.y = posY;

            this.inits.mapData[this.config.mapNumber][posY][posX].id = idPj;
        } catch (err) {
            dumpError(err);
        }
    };

    sign = x => {
        return x > 0 ? 1 : x < 0 ? -1 : 0;
    };
}

function dumpError(err) {
    if (typeof err === "object") {
        if (err.message) {
            console.log("\nMessage: " + err.message);
        }
        if (err.stack) {
            console.log("\nStacktrace:");
            console.log("====================");
            console.log(err.stack);
        }
    } else {
        console.log("dumpError :: argument is not an object");
    }
}

export default Engine;
